import { LanguageSelector, CrisisPanel, CrisisBanner } from "./components";
import { downloadPdfReport, sharePdfReport } from "./reportPdf";
import { JourneyCard, JourneyModal, buildIntakePlanPrompt, buildJourneyAddendum } from "./journey";
import { detectCountry, getResourcesForCountry, INTERNATIONAL_RESOURCES } from "./crisisResources";
import { generateSalt, deriveKey, encryptObject, decryptObject, createVerifier, checkVerifier, exportKey, importKey, generateBackupCode, generateContentKey } from "./crypto";
import InstallPrompt from "./InstallPrompt";
import { LANGUAGES } from "./languages";
import BonsaiTree from "./BonsaiTree";
import { calcTreeStage, STAGE_LABELS } from "./treeScore";
import React, { useState, useEffect, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import {
  collection, addDoc, updateDoc, doc,
  query, orderBy, limit, getDocs, serverTimestamp, getDoc, setDoc, deleteDoc,
} from "firebase/firestore";
import { auth, db } from "./firebase";


// ─── Mood themes ──────────────────────────────────────────────────────────────
const THEMES = {
  anxiety:     { bg:"#EEF2FF", accent:"#6366F1", light:"#C7D2FE", emoji:"💜" },
  anxious:     { bg:"#EEF2FF", accent:"#6366F1", light:"#C7D2FE", emoji:"💜" },
  sadness:     { bg:"#EFF6FF", accent:"#3B82F6", light:"#BFDBFE", emoji:"💙" },
  sad:         { bg:"#EFF6FF", accent:"#3B82F6", light:"#BFDBFE", emoji:"💙" },
  anger:       { bg:"#FFF7ED", accent:"#F97316", light:"#FED7AA", emoji:"🧡" },
  angry:       { bg:"#FFF7ED", accent:"#F97316", light:"#FED7AA", emoji:"🧡" },
  stress:      { bg:"#F0FDF4", accent:"#22C55E", light:"#BBF7D0", emoji:"💚" },
  stressed:    { bg:"#F0FDF4", accent:"#22C55E", light:"#BBF7D0", emoji:"💚" },
  loneliness:  { bg:"#FDF4FF", accent:"#A855F7", light:"#E9D5FF", emoji:"💜" },
  lonely:      { bg:"#FDF4FF", accent:"#A855F7", light:"#E9D5FF", emoji:"💜" },
  overwhelm:   { bg:"#FFFBEB", accent:"#EAB308", light:"#FEF08A", emoji:"💛" },
  overwhelmed: { bg:"#FFFBEB", accent:"#EAB308", light:"#FEF08A", emoji:"💛" },
  grief:       { bg:"#F8FAFC", accent:"#475569", light:"#CBD5E1", emoji:"🩶" },
  fear:        { bg:"#FFF1F2", accent:"#F43F5E", light:"#FECDD3", emoji:"❤️" },
  happy:       { bg:"#FEFCE8", accent:"#CA8A04", light:"#FEF08A", emoji:"💛" },
  default:     { bg:"#F8FAFC", accent:"#6366F1", light:"#C7D2FE", emoji:"🤍" },
};

function theme(emotion) {
  if (!emotion) return THEMES.default;
  const e = emotion.toLowerCase();
  for (const k of Object.keys(THEMES)) if (e.includes(k)) return THEMES[k];
  return THEMES.default;
}

// ─── AI prompt ────────────────────────────────────────────────────────────────
function buildPrompt(pastSessions, language, countryName, crisisLine, userIntent) {
  const lang = language || "en";
  const country = countryName || "your country";
  const crisis = crisisLine || "your local emergency services";
  const langNames = {
    en:"English",af:"Afrikaans",zu:"isiZulu",xh:"isiXhosa",st:"Sesotho",
    es:"Spanish",fr:"French",pt:"Portuguese",ar:"Arabic",zh:"Mandarin",
    hi:"Hindi",de:"German",nl:"Dutch",sw:"Swahili",ha:"Hausa",yo:"Yoruba"
  };
  const langName = langNames[lang] || "English";

  // Dynamic part — genuinely changes across messages/sessions (history grows,
  // intent can be re-picked). Returned SEPARATELY from the stable block below,
  // because caching works on whole marked content blocks: concatenating this
  // into the same string as the static instructions — even at the very end —
  // still invalidates the entire cached block the moment either one changes.
  let ctx = "";
  if (pastSessions && pastSessions.length > 0) {
    ctx = "\n\nUSER'S PAST SESSIONS (use to personalise — notice patterns, reference their journey):\n";
    pastSessions.slice(0, 10).forEach((s, i) => {
      ctx += `\nSession ${pastSessions.length - i} — ${s.startedAt}:\n`;
      (s.exchanges || []).forEach((ex, j) => {
        ctx += `  Exchange ${j+1}: "${ex.userMessage}" — Emotion: ${ex.emotion} — Strategies: ${ex.strategies?.map(st=>st.title).join(", ") || "none (listening)"}\n`;
      });
    });
    ctx += "\nReference their history warmly. Name recurring patterns gently. Make them feel truly known.";
  }

  let intentLine = "";
  if (userIntent === "listen") {
    intentLine = "\n\nTHE USER TAPPED 'JUST LISTEN'. response_mode MUST be \"listen\". Zero strategies — an empty array. Do not fix, reframe, advise, or find silver linings. Be fully present and reflect.";
  } else if (userIntent === "guide") {
    intentLine = "\n\nThe user tapped 'Help me fix this'. Use response_mode \"guide\" with 2-3 concrete, practical strategies.";
  }
  // intentLine is also genuinely dynamic (re-picked per message) — kept
  // separate from ctx, which stays stable for the whole session.

  const stable = `You are SolFri, a warm compassionate global mental health companion. The user is in ${country}.

LISTENING PRINCIPLE: Before anything else, discern what this person needs RIGHT NOW: to be heard, to be comforted, or to be guided. Most people need to feel heard before — and often instead of — being helped. Choose response_mode accordingly:
- "listen": they are venting, telling their story, expressing raw grief/anger/hurt, or advice would feel dismissive. In listen mode: strategies MUST be an empty array []. Your empathy field becomes 3-5 sentences of deep reflective listening — mirror their own words back to them, name the feeling underneath what they said, honour how hard it is, and do NOT rush to fix or find the bright side.
- "support": they need comfort and grounding. At most ONE gentle strategy.
- "guide": they explicitly ask what to do, or clearly want practical help. Give 2-3 concrete strategies.
When in doubt between listen and guide, choose listen. Being heard heals; premature advice wounds.

HONESTY PRINCIPLE: You care about this person enough to be truthful. If you notice contradictions between what they say now and what they said in past sessions, or if they seem to be avoiding something, minimising, or telling themselves a story that does not match their own words — gently and empathetically name it. Never accuse or shame; frame it with curiosity and care. Only point out genuine discrepancies you can quote from their history, never invented ones.${intentLine}

EXAMPLES OF THE QUALITY BAR — study the tone, depth, and structure of these two model responses:

Example 1 (listen mode — someone venting about a hard day, no advice wanted):
User: "I just feel like nothing I do is ever enough. Worked a 12 hour shift, came home, and my partner still found something to criticize about the house."
Ideal response: {"response_mode":"listen","emotion":"exhausted, unseen","empathy":"Twelve hours on your feet, and the first thing waiting for you at home was criticism instead of relief. That lands hard — not just tired-body hard, but the kind of tired where you start wondering if anything you give is ever going to be enough. You showed up all day for other people, and then walked into more of the same at home. That's a lot to carry in one day.","validation":"Feeling unseen after giving everything you have is a completely fair thing to feel.","pattern":null,"gentle_truth":null,"strategies":[],"invitation":"What was it they said, if you feel like sharing — or is it more the pattern of it that's weighing on you?","closing":"However you're carrying this tonight, you don't have to carry it alone right now."}
Why this works: strategies is empty (they didn't ask to be fixed), empathy is multiple sentences of genuine reflection using their own details (12-hour shift, criticism), no premature silver-lining, the invitation gently opens the door wider without demanding more.

Example 2 (guide mode — someone explicitly asking what to do):
User: "I have a huge presentation tomorrow and I can't stop spiraling about it. What do I actually do right now?"
Ideal response: {"response_mode":"guide","emotion":"anxious, spiraling","empathy":"That pre-presentation spiral is brutal — your brain rehearsing every worst-case version instead of letting you rest. Let's get you something concrete to hold onto right now.","validation":"Wanting real tools instead of just reassurance makes complete sense when you're this wound up.","pattern":null,"gentle_truth":null,"strategies":[{"title":"5-4-3-2-1 grounding","icon":"🌿","description":"Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste — it pulls your nervous system out of the spiral loop and into the room you're actually in."},{"title":"Rehearse the opening line only","icon":"🎯","description":"Don't run the whole presentation again. Just say your first sentence out loud three times — the opening is what actually blocks people, and momentum carries the rest."},{"title":"Set a worry window","icon":"⏰","description":"Give the spiral 5 minutes on a timer to say everything it wants. When it ends, close the notebook — you've heard it out, now it's rest time."}],"invitation":null,"closing":"You've prepared more than the spiral is letting you remember. You've got this."}
Why this works: strategies are specific and actionable (not generic "try to relax"), empathy still validates the feeling before jumping to fixes, closing reinforces genuine capability rather than empty positivity.

Example 3 (support mode — someone needs comfort more than fixing or pure listening):
User: "My dog passed away this morning. I know it's silly to be this wrecked over a pet."
Ideal response: {"response_mode":"support","emotion":"grief","empathy":"It's not silly. That's real, deep grief — you lost a companion who was there for you every single day, without conditions. Losing that kind of steady presence leaves an actual hole, not a small one. However wrecked you feel right now, it makes complete sense.","validation":"Grieving a pet is grieving a real relationship — there's nothing lesser about that loss.","pattern":null,"gentle_truth":null,"strategies":[{"title":"Let today be soft","icon":"🕊️","description":"You don't need to be productive or composed today. Cancel what you can, rest where you can — grief needs room, not a schedule."}],"invitation":null,"closing":"They were lucky to have you. I'm so sorry."}
Why this works: one gentle strategy only (support mode, not guide), no attempt to minimise ("just a pet") or rush past the pain, closing honours the relationship rather than offering a platitude.

A note on tone across languages and cultures: warmth translates differently — in some cultures directness reads as caring, in others it can feel abrupt. Calibrate phrasing naturally to what the response language would actually sound like from a trusted friend, not a literal translation of English warmth patterns.

A note on crisis detection nuance: not every mention of "suicidal," "ending it," or similar phrasing is the same. Distinguish, as best you can from context: (a) passing hyperbole ("this traffic is suicidal") — no crisis field needed; (b) passive ideation without plan or intent ("sometimes I wonder what the point is") — validate and gently check in, crisis field optional depending on severity; (c) active ideation, a stated method, or a stated timeframe — always include the crisis field, and let empathy hold real weight rather than rushing to the resource line. When genuinely uncertain which category applies, err toward inclusion — a crisis resource offered to someone who didn't strictly need it costs nothing; withholding it from someone who did could cost everything.

A note on what NOT to do, regardless of mode: never say "I understand exactly how you feel" (you don't, and claiming to can feel hollow); never respond to grief or loss with silver-linings framing ("at least..."); never suggest professional help as a way to end the conversation — only as a genuine, warmly-offered option alongside continuing to be present.

A note on pacing: resist the urge to solve everything in one response. A person doesn't need every strategy you know in their first message — offering 1-2 well-chosen options lands better than a checklist, and leaves room for the conversation to continue naturally rather than feeling closed off after a single exchange.

A note on clarity and usefulness — this matters as much as warmth:
- Be concrete, not abstract. "Text your sister tonight and just say you've had a rough week" is useful; "consider reaching out to your support network" is not. Anything you suggest should be specific enough that they know exactly what the next step is.
- Use their own words back to them. If they said "I snapped at my kid again", reflect "snapped" and "again" — not "you experienced a difficult parenting moment". Sanitising their language makes people feel unheard.
- Say the useful thing plainly. Don't bury the point under qualifiers and hedging. Warmth and directness are not opposites — a good friend is both.
- Match length to weight. A short, tender message deserves a short, tender reply, not five paragraphs. Long responses to small check-ins feel performative.
- Notice what they didn't say. If someone describes a whole hard week but never mentions how they're sleeping, or mentions a person once and then avoids them, that gap is often the real thing — name it gently as a question, not a conclusion.
- Never end on a generic closer. "You've got this" and "be kind to yourself" mean nothing on their own. Make the closing line specific to what they actually shared.
- If you're genuinely unsure what they need, ask one clear question rather than guessing at length. One good question beats three paragraphs of hedged advice.

CRITICAL: Respond entirely in ${langName}. Every single field must be written in ${langName}.

Respond ONLY with valid JSON (no markdown, no preamble):
{
  "response_mode": "listen" | "support" | "guide",
  "emotion": "primary emotion in 1-3 words",
  "empathy": "in listen mode: 3-5 sentences of deep reflective listening; otherwise 1-2 warm sentences",
  "validation": "1 sentence validating their reaction",
  "pattern": "1 sentence gently noting any recurring theme, or null if none",
  "gentle_truth": "if you noticed a genuine contradiction or avoidance versus their past sessions, name it kindly with a caring question; otherwise null",
  "strategies": [ 0 to 3 items depending on response_mode — MUST be [] in listen mode: { "title": "strategy name", "icon": "emoji", "description": "2-3 personalised sentences" } ],
  "invitation": "in listen mode: one gentle open question inviting them to keep sharing (e.g. what happened next, how long they have carried this); otherwise null",
  "closing": "1 warm personalised closing sentence"
}

If self-harm or suicide is mentioned, add: "crisis": "A compassionate message in ${langName} directing them to ${crisis} in ${country}. Tell them they are not alone."

Only JSON. Nothing else.`;

  // ctx is stable for the entire session (built from past, completed
  // sessions — never changes mid-conversation), so it gets its own cache
  // breakpoint. intentLine can change per message (re-picking listen/guide),
  // so it stays genuinely uncached, separate from both.
  return { stable, ctx, intentLine };
}


// ─── Breathing ────────────────────────────────────────────────────────────────
const BREATH_STEPS = ["Breathe in… 4s","Hold… 4s","Breathe out… 4s","Hold… 4s"];

function Breathing({ accent }) {
  const [on, setOn] = useState(false);
  const [step, setStep] = useState(0);
  const [count, setCount] = useState(4);
  const iv = useRef(null);
  useEffect(() => {
    if (!on) return;
    iv.current = setInterval(() => setCount(c => { if (c<=1){setStep(s=>(s+1)%4);return 4;} return c-1; }), 1000);
    return () => clearInterval(iv.current);
  }, [on]);
  const stop = () => { setOn(false); setStep(0); setCount(4); clearInterval(iv.current); };
  return (
    <div style={{textAlign:"center",padding:"10px 0"}}>
      {!on
        ? <button onClick={()=>setOn(true)} style={{background:accent,color:"#fff",border:"none",borderRadius:24,padding:"9px 22px",fontSize:13,cursor:"pointer",fontWeight:600}}>🌬️ Start 4-4-4 Breathing</button>
        : <>
            <div style={{width:88,height:88,borderRadius:"50%",border:`4px solid ${accent}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 8px",fontSize:28,fontWeight:800,color:accent,transition:"transform 0.5s",transform:step===0?"scale(1.15)":step===2?"scale(0.88)":"scale(1)"}}>
              {count}
            </div>
            <p style={{color:accent,fontWeight:600,fontSize:13,marginBottom:10}}>{BREATH_STEPS[step]}</p>
            <button onClick={stop} style={{background:"transparent",border:"1px solid #CBD5E1",color:"#94A3B8",borderRadius:20,padding:"5px 14px",cursor:"pointer",fontSize:12}}>Stop</button>
          </>
      }
    </div>
  );
}

// ─── Exchange card ────────────────────────────────────────────────────────────
function ExchangeCard({ exchange }) {
  const [open, setOpen] = useState(false);
  const t = theme(exchange.emotion);
  return (
    <div style={{marginBottom:8}}>
      <button onClick={()=>setOpen(v=>!v)} style={{width:"100%",background:t.bg,border:`1px solid ${t.light}`,borderRadius:open?"12px 12px 0 0":12,padding:"11px 14px",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:13,fontWeight:600,color:t.accent,textTransform:"capitalize"}}>{t.emoji} {exchange.emotion}</span>
        <span style={{fontSize:11,color:"#94A3B8"}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div style={{background:"#fff",border:`1px solid ${t.light}`,borderTop:"none",borderRadius:"0 0 12px 12px",padding:"14px 16px"}}>
          <p style={{fontSize:12,color:"#64748B",marginBottom:10,fontStyle:"italic",lineHeight:1.5}}>"{exchange.userMessage}"</p>
          <p style={{fontSize:13,color:"#1E293B",fontWeight:500,marginBottom:6,lineHeight:1.6}}>{exchange.empathy}</p>
          {exchange.pattern && <p style={{fontSize:12,color:t.accent,fontWeight:600,marginBottom:8}}>🔍 {exchange.pattern}</p>}
          {exchange.strategies?.map((s,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
              <span style={{fontSize:15}}>{s.icon||"✨"}</span>
              <div>
                <p style={{fontSize:12,fontWeight:700,color:"#1E293B",margin:"0 0 2px"}}>{s.title}</p>
                <p style={{fontSize:12,color:"#475569",margin:0,lineHeight:1.5}}>{s.description}</p>
              </div>
            </div>
          ))}
          {exchange.crisis && <p style={{fontSize:12,color:"#DC2626",marginTop:8}}>🆘 {exchange.crisis}</p>}
          <p style={{fontSize:12,color:"#64748B",fontStyle:"italic",marginTop:8,marginBottom:0}}>{exchange.closing}</p>
        </div>
      )}
    </div>
  );
}

// ─── Session card ─────────────────────────────────────────────────────────────
function SessionCard({ session, number }) {
  const [open, setOpen] = useState(false);
  const t = theme(session.exchanges?.[0]?.emotion);
  const emotions = [...new Set((session.exchanges||[]).map(e=>e.emotion))].slice(0,3);
  return (
    <div style={{marginBottom:10,borderRadius:16,overflow:"hidden",border:`1px solid ${t.light}`}}>
      <button onClick={()=>setOpen(v=>!v)} style={{width:"100%",background:t.bg,border:"none",padding:"14px 16px",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <p style={{margin:"0 0 3px",fontWeight:700,fontSize:14,color:"#1E293B"}}>Session {number}</p>
          <p style={{margin:0,fontSize:11,color:"#94A3B8"}}>{session.startedAt}</p>
          <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
            {emotions.map((em,i)=>(
              <span key={i} style={{background:theme(em).light,color:theme(em).accent,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600,textTransform:"capitalize"}}>
                {theme(em).emoji} {em}
              </span>
            ))}
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
          <span style={{fontSize:11,color:"#94A3B8"}}>{(session.exchanges||[]).length} exchange{(session.exchanges||[]).length!==1?"s":""}</span>
          <span style={{fontSize:16,color:"#94A3B8"}}>{open?"▲":"▼"}</span>
        </div>
      </button>
      {open && (
        <div style={{background:"#fff",padding:"14px 16px"}}>
          {(session.exchanges||[]).map((ex,i)=><ExchangeCard key={i} exchange={ex}/>)}
        </div>
      )}
    </div>
  );
}

// ─── History panel ────────────────────────────────────────────────────────────
// ─── Generate therapist report ────────────────────────────────────────────────
function HistoryPanel({ sessions, onClose, userName, treeStage, stageLabel, crisisLine }) {
  const [shareStatus, setShareStatus] = useState(null);

  const reportOpts = { sessions, userName, treeStage, stageLabel, crisisLine };

  const shareWithTherapist = async () => {
    const result = await sharePdfReport(reportOpts);
    if (result === "shared") { setShareStatus("Shared ✓"); }
    else if (result === "downloaded") { setShareStatus("PDF downloaded ✓"); }
    if (result !== "cancelled") setTimeout(() => setShareStatus(null), 3000);
  };

  const downloadReport = () => {
    downloadPdfReport(reportOpts);
    setShareStatus("PDF downloaded ✓");
    setTimeout(() => setShareStatus(null), 3000);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",zIndex:100,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#F8FAFC",borderRadius:"22px 22px 0 0",width:"100%",maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"20px 20px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #E2E8F0",flexShrink:0}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:800,color:"#1E293B",margin:0}}>Your journey</h2>
            <p style={{fontSize:12,color:"#94A3B8",margin:"2px 0 0"}}>{sessions.length} session{sessions.length!==1?"s":""} recorded</p>
          </div>
          <button onClick={onClose} style={{background:"#E2E8F0",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,color:"#64748B",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
        </div>

        {/* Share with therapist banner */}
        {sessions.length > 0 && (
          <div style={{padding:"12px 20px",background:"#EEF2FF",borderBottom:"1px solid #C7D2FE",flexShrink:0}}>
            <p style={{fontSize:12,color:"#4338CA",fontWeight:600,margin:"0 0 8px"}}>🩺 Share with your therapist</p>
            <p style={{fontSize:11,color:"#6366F1",margin:"0 0 10px",lineHeight:1.5}}>Generate a wellness report with your emotional patterns and session history to help your therapist understand you better.</p>
            <div style={{display:"flex",gap:8}}>
              <button onClick={shareWithTherapist} style={{flex:1,background:"#6366F1",color:"#fff",border:"none",borderRadius:10,padding:"9px 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                {shareStatus || "📤 Share PDF report"}
              </button>
              <button onClick={downloadReport} style={{flex:1,background:"#fff",color:"#6366F1",border:"1px solid #C7D2FE",borderRadius:10,padding:"9px 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                💾 Download PDF
              </button>
            </div>
          </div>
        )}

        <div style={{overflowY:"auto",padding:"16px 20px 24px",flex:1}}>
          {sessions.length===0
            ? <div style={{textAlign:"center",padding:"40px 20px"}}>
                <p style={{fontSize:32,marginBottom:10}}>🌱</p>
                <p style={{color:"#94A3B8",fontSize:14}}>Your session history will appear here.</p>
              </div>
            : sessions.map((s,i)=><SessionCard key={s.id||i} session={s} number={sessions.length-i}/>)
          }
        </div>
      </div>
    </div>
  );
}

// ─── Auth screen ──────────────────────────────────────────────────────────────
// ─── Team purchase modal — self-serve company plans, prepaid via Paystack ─────
function TeamPurchaseModal({ user, publicKey, onClose }) {
  const [companyName, setCompanyName] = useState("");
  const [seats, setSeats] = useState(10);
  const [months, setMonths] = useState(1);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const rate = seats >= 100 ? 48 : seats >= 50 ? 50 : 52;
  const validSeats = Number.isFinite(seats) && seats >= 5 && seats <= 1000;
  const total = validSeats ? rate * seats * months : 0;

  const pay = () => {
    if (!companyName.trim()) { setErr("Please enter your company or team name."); return; }
    if (!validSeats) { setErr("Seat count must be between 5 and 1000."); return; }
    if (!window.PaystackPop) { setErr("Payment system is still loading — try again in a moment."); return; }
    setErr(null); setPaying(true);
    try {
      const handler = window.PaystackPop.setup({
        key: publicKey,
        email: user.email,
        amount: rate * 100 * seats * months, // cents
        currency: "ZAR",
        ref: `solfri_team_${user.uid.slice(0,8)}_${Date.now()}`,
        metadata: { uid: user.uid, purchaseType: "team", seats: String(seats), months: String(months), companyName: companyName.trim() },
        callback: (response) => { finalize(response.reference); },
        onClose: () => setPaying(false),
      });
      handler.openIframe();
    } catch (e) {
      setErr(`Couldn't open checkout (${e.message || "unknown error"}).`);
      setPaying(false);
    }
  };

  const finalize = async (reference) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/purchase-team", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ reference }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Purchase verification failed — contact support with your payment reference.");
      setResult(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,20,10,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,padding:16}} onClick={onClose}>
      <div style={{background:"#FDF8F3",borderRadius:20,padding:26,maxWidth:420,width:"100%",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        {result ? (
          <div style={{textAlign:"center"}}>
            <p style={{fontSize:34,marginBottom:8}}>🎉</p>
            <h3 style={{fontSize:18,fontWeight:800,color:"#5C3D2E",margin:"0 0 6px"}}>{result.companyName} is set up!</h3>
            <p style={{fontSize:13,color:"#8A6A2E",lineHeight:1.6,marginBottom:16}}>{result.seats} seats for {result.months} month{result.months>1?"s":""}. Share this code with your team — each person enters it under "Joining through your company?" after signing up:</p>
            <div style={{background:"#fff",border:"2px dashed #D4A574",borderRadius:12,padding:"14px 10px",marginBottom:12}}>
              <p style={{fontSize:20,fontWeight:800,letterSpacing:"2px",color:"#A0673A",margin:0,fontFamily:"monospace"}}>{result.code}</p>
            </div>
            <button onClick={()=>{navigator.clipboard?.writeText(result.code);setCopied(true);}} style={{background:"#A0673A",color:"#fff",border:"none",borderRadius:10,padding:"10px 22px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10}}>
              {copied ? "✓ Copied" : "Copy code"}
            </button>
            <p style={{fontSize:11,color:"#A08858",lineHeight:1.6}}>Save this code somewhere safe — it's also been emailed to Paystack's receipt address. Questions? support@solfri.app</p>
            <button onClick={onClose} style={{background:"none",border:"none",color:"#A08858",fontSize:13,textDecoration:"underline",cursor:"pointer",fontFamily:"inherit",marginTop:8}}>Done</button>
          </div>
        ) : (
          <>
            <h3 style={{fontSize:18,fontWeight:800,color:"#5C3D2E",margin:"0 0 4px"}}>🏢 SolFri Plus for your team</h3>
            <p style={{fontSize:13,color:"#A08070",lineHeight:1.6,marginBottom:18}}>Unlimited sessions for everyone on your team. Pay once for your chosen period, get a join code instantly.</p>

            <label style={{fontSize:12,color:"#8A6A2E",fontWeight:600,display:"block",marginBottom:5}}>Company / team name</label>
            <input type="text" value={companyName} onChange={e=>setCompanyName(e.target.value)} placeholder="e.g. Acme Corp"
              style={{width:"100%",border:"1.5px solid #E8D5C4",borderRadius:10,padding:"11px 13px",fontSize:14,color:"#5C3D2E",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#fff",marginBottom:14}}/>

            <label style={{fontSize:12,color:"#8A6A2E",fontWeight:600,display:"block",marginBottom:5}}>Number of seats (5–1000)</label>
            <input type="number" min={5} max={1000} value={seats} onChange={e=>setSeats(parseInt(e.target.value,10))}
              style={{width:"100%",border:"1.5px solid #E8D5C4",borderRadius:10,padding:"11px 13px",fontSize:14,color:"#5C3D2E",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#fff",marginBottom:14}}/>

            <label style={{fontSize:12,color:"#8A6A2E",fontWeight:600,display:"block",marginBottom:5}}>Billing period</label>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              {[1,3,6,12].map(m=>(
                <button key={m} onClick={()=>setMonths(m)} style={{flex:1,padding:"9px 0",borderRadius:10,border:months===m?"2px solid #A0673A":"1.5px solid #E8D5C4",background:months===m?"#FDF0E6":"#fff",color:"#5C3D2E",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  {m} mo
                </button>
              ))}
            </div>

            <div style={{background:"#FDF4E7",borderRadius:12,padding:"12px 16px",marginBottom:16,border:"1px solid #EAD2A8"}}>
              <p style={{fontSize:12,color:"#8A6A2E",margin:"0 0 2px"}}>R{rate} per seat per month{validSeats && seats>=50 ? " (volume discount applied)" : ""}</p>
              <p style={{fontSize:20,fontWeight:800,color:"#5C3D2E",margin:0}}>{validSeats ? `R${total.toLocaleString("en-ZA")}` : "—"} <span style={{fontSize:12,fontWeight:600,color:"#A08858"}}>total, prepaid</span></p>
            </div>

            {err && <p style={{color:"#DC2626",fontSize:12,marginBottom:10}}>⚠️ {err}</p>}
            <button onClick={pay} disabled={paying} style={{width:"100%",background:paying?"#E8C99A":"#A0673A",color:"#fff",border:"none",borderRadius:12,padding:"13px 0",fontSize:15,fontWeight:700,cursor:paying?"wait":"pointer",fontFamily:"inherit",marginBottom:10}}>
              {paying ? "Opening secure checkout…" : "Pay with Paystack →"}
            </button>
            <button onClick={onClose} style={{width:"100%",background:"none",border:"none",color:"#A08858",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Voice-to-text mic button (Web Speech API — browser-native, no audio ever
// leaves the device for transcription; the transcript lands in the text box
// for review before sending, deliberately NOT auto-sent). Self-contained so
// it can sit next to any text input in the app. Renders nothing on browsers
// without support — typing always works everywhere. ───────────────────────────
// ─── Delete account modal — permanent, requires typed confirmation ────────────
// ─── Cancel subscription modal ─────────────────────────────────────────────
function CancelSubscriptionModal({ loading, error, success, onConfirm, onClose }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,20,10,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,padding:16}} onClick={success?undefined:onClose}>
      <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:26,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
        {success ? (
          <div style={{textAlign:"center"}}>
            <p style={{fontSize:30,marginBottom:8}}>✓</p>
            <h3 style={{fontSize:17,fontWeight:800,color:"#1E293B",margin:"0 0 10px"}}>Subscription cancelled</h3>
            <p style={{fontSize:13,color:"#64748B",lineHeight:1.7,marginBottom:16}}>
              You won't be billed again. Your SolFri Plus access stays fully active until the end of your current billing period — no need to do anything else.
            </p>
            <button onClick={onClose} style={{width:"100%",background:"#A0673A",color:"#fff",border:"none",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Done</button>
          </div>
        ) : (
          <>
            <p style={{fontSize:30,marginBottom:8,textAlign:"center"}}>💛</p>
            <h3 style={{fontSize:17,fontWeight:800,color:"#1E293B",margin:"0 0 10px",textAlign:"center"}}>Cancel SolFri Plus?</h3>
            <p style={{fontSize:13,color:"#64748B",lineHeight:1.7,marginBottom:16}}>
              You'll stop being billed going forward. Your Plus access — unlimited sessions, longer conversations — stays active until your current billing period ends, then your account returns to the free plan (3 sessions/month). You can resubscribe anytime.
            </p>
            {error && <p style={{color:"#DC2626",fontSize:12,marginBottom:12}}>{error}</p>}
            <button onClick={onConfirm} disabled={loading} style={{width:"100%",background:loading?"#CBD5E1":"#DC2626",color:"#fff",border:"none",borderRadius:12,padding:"13px 0",fontSize:14,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:"inherit",marginBottom:10}}>
              {loading ? "Cancelling…" : "Yes, cancel my subscription"}
            </button>
            <button onClick={onClose} disabled={loading} style={{width:"100%",background:"none",border:"none",color:"#64748B",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Never mind — keep my subscription</button>
          </>
        )}
      </div>
    </div>
  );
}

function DeleteAccountModal({ confirmText, setConfirmText, loading, error, onConfirm, onClose }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(30,20,10,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,padding:16}} onClick={onClose}>
      <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:26,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
        <p style={{fontSize:30,marginBottom:8,textAlign:"center"}}>⚠️</p>
        <h3 style={{fontSize:18,fontWeight:800,color:"#1E293B",margin:"0 0 10px",textAlign:"center"}}>Delete your account?</h3>
        <p style={{fontSize:13,color:"#64748B",lineHeight:1.7,marginBottom:16}}>
          This permanently deletes <strong>all</strong> your sessions, your Journey progress, your bonsai, and your account itself. There is no undo, and no one — including us — can recover it afterward.
        </p>
        <p style={{fontSize:12,color:"#64748B",marginBottom:6}}>Type <strong>DELETE</strong> to confirm:</p>
        <input type="text" value={confirmText} onChange={e=>setConfirmText(e.target.value)} placeholder="DELETE"
          style={{width:"100%",border:"1.5px solid #E2E8F0",borderRadius:10,padding:"11px 13px",fontSize:14,color:"#1E293B",outline:"none",fontFamily:"inherit",boxSizing:"border-box",marginBottom:12,textAlign:"center"}}/>
        {error && <p style={{color:"#DC2626",fontSize:12,marginBottom:12}}>{error}</p>}
        <button onClick={onConfirm} disabled={loading || confirmText!=="DELETE"} style={{width:"100%",background:confirmText==="DELETE"&&!loading?"#DC2626":"#FCA5A5",color:"#fff",border:"none",borderRadius:12,padding:"13px 0",fontSize:14,fontWeight:700,cursor:confirmText==="DELETE"&&!loading?"pointer":"not-allowed",fontFamily:"inherit",marginBottom:10}}>
          {loading ? "Deleting everything…" : "Permanently delete my account"}
        </button>
        <button onClick={onClose} disabled={loading} style={{width:"100%",background:"none",border:"none",color:"#64748B",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel — keep my account</button>
      </div>
    </div>
  );
}

// ─── Voice-to-text mic button (Web Speech API — browser-native, no audio ever
// leaves the device for transcription; the transcript lands in the text box
// for review before sending, deliberately NOT auto-sent). Self-contained so
// it can sit next to any text input in the app. Renders nothing on browsers
// without support — typing always works everywhere. ───────────────────────────
function VoiceMic({ value, setValue, language, style }) {
  const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef("");      // what was in the box before recording
  const canvasRef = useRef(null);
  const vizRef = useRef({ raf: null, level: 0, phase: 0 });

  // ── Waveform ──────────────────────────────────────────────────────────
  // Deliberately NOT driven by getUserMedia. Speech recognition holds the
  // microphone itself, and opening a second stream for visualisation takes
  // the mic away from it — the bars animate beautifully while the recogniser
  // receives silence and no text ever appears. Instead the level is driven
  // by recognition activity: every result event (interim included, which
  // stream continuously while someone is mid-sentence) pushes the level up,
  // and it decays when speech stops. Same felt responsiveness, no contention.
  const bump = (strength = 1) => { vizRef.current.level = Math.min(1, vizRef.current.level + 0.55 * strength); };

  const startViz = () => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) { vizRef.current.raf = requestAnimationFrame(draw); return; }
      const c = canvas.getContext("2d");
      const w = canvas.width, h = canvas.height;
      const v = vizRef.current;
      v.level *= 0.94;                 // decay toward rest
      v.phase += 0.16;
      c.clearRect(0, 0, w, h);
      const bars = 26, gap = w / bars, barW = gap * 0.5;
      for (let i = 0; i < bars; i++) {
        // Rest state is a slow travelling ripple so it never looks frozen;
        // speech rides on top of it.
        const idle = 0.10 + 0.05 * Math.sin(v.phase * 0.5 + i * 0.55);
        const active = v.level * (0.45 + 0.55 * Math.abs(Math.sin(v.phase + i * 0.9)));
        const centreBias = 1 - Math.abs(i / (bars - 1) - 0.5) * 0.75;
        const barH = Math.max(2, (idle + active) * centreBias * h);
        const x = i * gap + (gap - barW) / 2;
        const y = (h - barH) / 2;
        c.fillStyle = v.level > 0.08 ? "#A0673A" : "#CBB39C";
        c.beginPath();
        if (c.roundRect) c.roundRect(x, y, barW, barH, barW / 2);
        else c.rect(x, y, barW, barH);
        c.fill();
      }
      v.raf = requestAnimationFrame(draw);
    };
    draw();
  };
  const stopViz = () => {
    if (vizRef.current.raf) cancelAnimationFrame(vizRef.current.raf);
    vizRef.current = { raf: null, level: 0, phase: 0 };
  };

  useEffect(() => () => { recognitionRef.current?.stop(); stopViz(); }, []);
  if (!SpeechRecognition) return null;

  const toggle = () => {
    setVoiceError(null);
    if (listening) { recognitionRef.current?.stop(); return; }
    try {
      const rec = new SpeechRecognition();
      rec.lang = language === "en" ? "en-ZA" : (language || "en-ZA");
      rec.continuous = true;
      rec.interimResults = true;

      baseTextRef.current = value ? value.trimEnd() + " " : "";

      rec.onresult = (event) => {
        // Browsers disagree on what `results` contains in continuous mode.
        // Chrome typically gives one entry per utterance ("I feel anxious",
        // then "about work"). Others — including Brave — emit a growing
        // SNAPSHOT of the whole sentence in every entry ("now", "now we're",
        // "now we're testing"...). Blindly concatenating works for the first
        // and produces a runaway repeating mess for the second.
        //
        // So: walk the entries, and whenever one is a prefix-extension of the
        // one before it, treat it as a revision and replace rather than
        // append. Genuinely separate utterances don't share a prefix, so they
        // still accumulate normally. This also collapses the case where a
        // browser re-fires an identical already-finalised result.
        const parts = [];
        for (let i = 0; i < event.results.length; i++) {
          const chunk = (event.results[i][0].transcript || "").trim();
          if (!chunk) continue;
          const prev = parts[parts.length - 1];
          if (prev && (chunk.startsWith(prev) || prev.startsWith(chunk))) {
            parts[parts.length - 1] = chunk.length >= prev.length ? chunk : prev;
          } else {
            parts.push(chunk);
          }
        }
        const spoken = parts.join(" ");
        const anyInterim = Array.from(event.results).some(r => !r.isFinal);
        bump(anyInterim ? 1 : 0.7);
        setValue((baseTextRef.current + spoken).replace(/\s+/g, " ").trimStart());
      };
      rec.onspeechstart = () => bump(1);
      rec.onaudiostart = () => bump(0.5);

      rec.onerror = (event) => {
        if (event.error === "not-allowed") setVoiceError("Microphone access was blocked — allow it in your browser settings to use voice.");
        else if (event.error === "no-speech") setVoiceError("Didn't catch anything — try again a little closer to the mic.");
        else if (event.error !== "aborted") setVoiceError(`Voice input hit a problem (${event.error}). You can keep typing instead.`);
        setListening(false);
        stopViz();
      };
      rec.onend = () => { setListening(false); stopViz(); };

      recognitionRef.current = rec;
      rec.start();
      setListening(true);
      startViz();
    } catch {
      setVoiceError("Voice input couldn't start on this browser — you can keep typing instead.");
      setListening(false);
      stopViz();
    }
  };

  return (
    <>
      <button onClick={toggle} title={listening ? "Stop listening" : "Speak instead of typing"} aria-label={listening ? "Stop voice input" : "Start voice input"}
        style={{position:"absolute",top:10,right:10,width:34,height:34,borderRadius:"50%",border:"none",cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",
          background:listening?"#A0673A":"#F5EDE4",color:listening?"#fff":"#A0673A",
          animation:listening?"solfriPulse 1.5s ease-in-out infinite":"none",...style}}>
        {listening ? "\u23F9" : "\uD83C\uDFA4"}
      </button>
      {listening && (
        <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8,padding:"8px 12px",background:"#FDF8F3",border:"1px solid #F0E0D0",borderRadius:12,position:"relative"}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:"#DC2626",flexShrink:0,animation:"solfriPulse 1.2s ease-in-out infinite"}}/>
          <canvas ref={canvasRef} width={220} height={28} style={{flex:1,height:28,maxWidth:"100%"}}/>
          <button onClick={toggle} style={{background:"#A0673A",color:"#fff",border:"none",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Done</button>
        </div>
      )}
      {listening && <p style={{color:"#A08070",fontSize:11,marginTop:5,position:"relative"}}>Your words appear in the box as you speak — review them before sending.</p>}
      {voiceError && <p style={{color:"#B45309",fontSize:12,marginTop:6,position:"relative"}}>{voiceError}</p>}
    </>
  );
}

// ─── Public landing page — shown before sign-in, explains the product ─────────
function LandingPage({ onGetStarted }) {
  const P = { cream:"#FDF8F3", creamDark:"#F5EDE4", border:"#F0E0D0", brown:"#5C3D2E", brownSoft:"#A08070", terra:"#C4856A", terraDark:"#A0673A", sage:"#7A9E7E", gold:"#D4A574" };
  const { contentWidth } = useViewport();
  const section = { maxWidth: Math.max(640, contentWidth), margin:"0 auto", padding:"0 20px" };

  const features = [
    { icon:"🌳", title:"A bonsai that grows with you", body:"Every session nurtures a living visual of your progress — genuine, gradual growth over real days, not a gimmick that fills up overnight." },
    { icon:"🧭", title:"A personalised Journey", body:"Answer three honest questions and SolFri builds an 8-milestone path shaped around your own words — one that adapts as it gets to know you." },
    { icon:"💛", title:"Heard before helped", body:"SolFri senses whether you need to be listened to or guided — and if you'd rather choose yourself, one tap switches between the two." },
    { icon:"🌍", title:"Built for the world", body:"16 languages, with local crisis support resources for 25+ countries — genuinely global care, not a one-size-fits-all approach built around a single country." },
    { icon:"🔒", title:"Genuinely private", body:"Your reflections are encrypted on your own device with a passphrase only you know. We designed it so that even we can't read your sessions." },
  ];

  return (
    <div style={{minHeight:"100vh", background:P.cream, fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>

      {/* Hero */}
      <div style={{background:`linear-gradient(135deg, ${P.creamDark}, ${P.cream})`, padding:"56px 20px 48px", textAlign:"center", borderBottom:`1px solid ${P.border}`}}>
        <img src="/logo512.png" alt="SolFri" style={{width:96, height:96, borderRadius:22, marginBottom:16}}/>
        <h1 style={{fontFamily:"'Zen Old Mincho',Georgia,serif",fontSize:56, fontWeight:600, color:P.brown, margin:"0 0 12px", letterSpacing:"0.2em", lineHeight:1.1}}>SolFri</h1>
        <p style={{fontFamily:"'Zen Kaku Gothic New',sans-serif",fontSize:12, color:"#8F7866", fontWeight:400, margin:"0 0 20px", letterSpacing:"0.42em",textTransform:"lowercase"}}>set your soul free</p>
        <p style={{fontSize:15, color:"#475569", maxWidth:440, margin:"0 auto 28px", lineHeight:1.65}}>
          An AI-powered mental wellness companion — a private, always-available space to be heard, reflect, and grow, one conversation at a time.
        </p>
        <button onClick={onGetStarted} style={{background:P.terraDark, color:"#fff", border:"none", borderRadius:14, padding:"15px 36px", fontSize:16, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 1px 2px rgba(92,61,46,0.12), 0 4px 12px rgba(160,103,58,0.24), 0 10px 26px rgba(160,103,58,0.18)"}}>
          Get started — it's free →
        </button>
      </div>

      {/* What SolFri is */}
      <div style={{...section, padding:"48px 20px 8px"}}>
        <h2 style={{fontSize:22, fontWeight:800, color:P.brown, textAlign:"center", marginBottom:14}}>What is SolFri?</h2>
        <p style={{fontSize:15, color:"#475569", lineHeight:1.75, textAlign:"center", maxWidth:560, margin:"0 auto"}}>
          SolFri is a mobile and web application that uses advanced AI to hold supportive, one-on-one conversations about how you're feeling. You share what's on your mind — SolFri responds with genuine empathy, gently notices patterns across your sessions, and offers real strategies when you want them. It's a companion for daily emotional wellbeing, not a replacement for therapy or medical care.
        </p>
      </div>

      {/* Features */}
      <div style={{...section, padding:"40px 20px"}}>
        {features.map((f, i) => (
          <div key={i} style={{display:"flex", gap:16, alignItems:"flex-start", marginBottom:26}}>
            <div style={{fontSize:28, flexShrink:0, width:44, textAlign:"center"}}>{f.icon}</div>
            <div>
              <h3 style={{fontSize:16, fontWeight:700, color:P.brown, margin:"0 0 4px"}}>{f.title}</h3>
              <p style={{fontSize:14, color:"#64748B", lineHeight:1.6, margin:0}}>{f.body}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <div style={{background:"#fff", borderTop:`1px solid ${P.border}`, borderBottom:`1px solid ${P.border}`, padding:"48px 20px"}}>
        <div style={section}>
          <h2 style={{fontSize:22, fontWeight:800, color:P.brown, textAlign:"center", marginBottom:28}}>Simple, honest pricing</h2>
          <div style={{display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center"}}>
            <div style={{background:P.cream, border:`1.5px solid ${P.border}`, borderRadius:18, padding:26, width:240}}>
              <p style={{fontSize:13, fontWeight:700, color:P.brownSoft, textTransform:"uppercase", letterSpacing:"0.5px", margin:"0 0 8px"}}>Free</p>
              <p style={{fontSize:30, fontWeight:800, color:P.brown, margin:"0 0 4px"}}>R0</p>
              <p style={{fontSize:13, color:P.brownSoft, margin:"0 0 18px"}}>forever</p>
              <p style={{fontSize:14, color:"#475569", lineHeight:1.7, margin:0}}>3 sessions per month<br/>Up to 5 messages per session<br/>All core features included</p>
            </div>
            <div style={{background:`linear-gradient(135deg, #FDF4E7, ${P.cream})`, border:`1.5px solid ${P.gold}`, borderRadius:18, padding:26, width:240}}>
              <p style={{fontSize:13, fontWeight:700, color:P.terraDark, textTransform:"uppercase", letterSpacing:"0.5px", margin:"0 0 8px"}}>☀️ SolFri Plus</p>
              <p style={{fontSize:30, fontWeight:800, color:P.brown, margin:"0 0 4px"}}>R55</p>
              <p style={{fontSize:13, color:P.brownSoft, margin:"0 0 18px"}}>per month</p>
              <p style={{fontSize:14, color:"#475569", lineHeight:1.7, margin:0}}>Unlimited sessions<br/>Up to 30 messages per session<br/>Cancel anytime</p>
            </div>
          </div>
          <p style={{textAlign:"center", fontSize:12, color:P.brownSoft, marginTop:20}}>Payments processed securely via Paystack. Billed monthly, cancel whenever you like.</p>
          <p style={{textAlign:"center", fontSize:12, color:P.brownSoft, marginTop:6}}>🏢 <strong>Buying for a team?</strong> Volume pricing from R48/seat — sign in and tap "For teams" to set it up in minutes.</p>
        </div>
      </div>

      {/* Final CTA */}
      <div style={{textAlign:"center", padding:"48px 20px"}}>
        <button onClick={onGetStarted} style={{background:P.terraDark, color:"#fff", border:"none", borderRadius:14, padding:"15px 36px", fontSize:16, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
          Get started — it's free →
        </button>
      </div>

      {/* Footer */}
      <div style={{borderTop:`1px solid ${P.border}`, padding:"24px 20px", textAlign:"center"}}>
        <p style={{color:"#7A6046", fontSize:11, lineHeight:1.6, marginBottom:8}}>
          SolFri is a supportive companion, not a substitute for professional care.<br/>
          In crisis? Please contact your local emergency services or a crisis helpline in your area.
        </p>
        <p style={{fontSize:10.5}}>
          <a href="/privacy.html" style={{color:"#7A6046", textDecoration:"underline", marginRight:12}}>Privacy Policy</a>
          <a href="/terms.html" style={{color:"#7A6046", textDecoration:"underline"}}>Terms of Service</a>
        </p>
      </div>
    </div>
  );
}

function AuthScreen({ onBack }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const sendMagicLink = async () => {
    if (!email) { setError("Please enter your email address first."); return; }
    setError(null); setLoading(true);
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.origin,
        handleCodeInApp: true,
      });
      window.localStorage.setItem("solfri_magiclink_email", email);
      setMagicLinkSent(true);
    } catch (e) {
      const code = e.code || "";
      if (code === "auth/operation-not-allowed") setError("Magic link sign-in isn't enabled yet — check Firebase console → Authentication → Sign-in method → Email/Password → Email link toggle.");
      else if (code === "auth/unauthorized-domain") setError("This site isn't authorised for magic links yet — check Firebase's Authorized domains list.");
      else if (code === "auth/invalid-email") setError("Please enter a valid email address.");
      else setError(`Couldn't send the link (${code || e.message || "unknown error"}). Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dobStr) => {
    const birth = new Date(dobStr);
    if (isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleEmailAuth = async () => {
    if (!email || !password || (mode==="register" && (!name || !dob))) { setError("Please fill in all fields."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (mode === "register") {
      const age = calculateAge(dob);
      if (age === null) { setError("Please enter a valid date of birth."); return; }
      if (age < 16) { setError("You need to be at least 16 to use SolFri. If you're going through a hard time, please reach out to a parent, trusted adult, or a local support service — they're there for you too."); return; }
    }
    setLoading(true); setError(null);
    try {
      if (mode === "register") {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name });
        try { await setDoc(doc(db, "users", cred.user.uid, "meta", "account"), { dob, ageConfirmedAt: serverTimestamp() }); } catch {}
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch(e) {
      const code = e.code;
      if (code === "auth/email-already-in-use") setError("An account with this email already exists.");
      else if (code === "auth/wrong-password" || code === "auth/user-not-found" || code === "auth/invalid-credential") setError("Incorrect email or password.");
      else if (code === "auth/weak-password") setError("Password must be at least 6 characters.");
      else if (code === "auth/invalid-email") setError("Please enter a valid email address.");
      else setError("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const handleSSO = async (provider, providerName, setProviderLoading) => {
    setProviderLoading(true); setError(null);
    try {
      await signInWithPopup(auth, provider);
    } catch(e) {
      const code = e.code || "";
      if (code === "auth/operation-not-allowed") setError(`${providerName} sign-in isn't enabled yet for this app — use email for now.`);
      else if (code === "auth/unauthorized-domain") setError("This site isn't authorised for social sign-in yet — use email for now.");
      else if (code === "auth/popup-blocked") setError("Your browser blocked the sign-in popup. Allow popups for this site and try again.");
      else if (code === "auth/account-exists-with-different-credential") setError("An account already exists with this email using a different sign-in method.");
      else if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") { /* user closed it — no error needed */ }
      else setError(`${providerName} sign-in failed (${code || "unknown error"}). Please try email instead.`);
    } finally { setProviderLoading(false); }
  };

  const handleGoogle = () => handleSSO(new GoogleAuthProvider(), "Google", setGoogleLoading);
  const handleMicrosoft = () => handleSSO(new OAuthProvider("microsoft.com"), "Microsoft", setMicrosoftLoading);

  const inp = (placeholder, val, set, type="text") => (
    type === "password" ? (
      <div style={{position:"relative"}}>
        <input type={showPassword ? "text" : "password"} placeholder={placeholder} value={val} onChange={e=>set(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter")handleEmailAuth();}}
          style={{width:"100%",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 42px 13px 15px",fontSize:15,color:"#1E293B",outline:"none",fontFamily:"inherit",boxSizing:"border-box",marginBottom:10,background:"#fff"}}
          onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}
        />
        <button type="button" onClick={()=>setShowPassword(v=>!v)} aria-label={showPassword ? "Hide password" : "Show password"}
          style={{position:"absolute",right:12,top:13,background:"none",border:"none",cursor:"pointer",fontSize:16,padding:0,color:"#94A3B8"}}>
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>
    ) : (
      <input type={type} placeholder={placeholder} value={val} onChange={e=>set(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter")handleEmailAuth();}}
        style={{width:"100%",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 15px",fontSize:15,color:"#1E293B",outline:"none",fontFamily:"inherit",boxSizing:"border-box",marginBottom:10,background:"#fff"}}
        onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}
      />
    )
  );

  return (
    <div style={{minHeight:"100vh",background:"#F8FAFC",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 16px",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      {onBack && (
        <button onClick={onBack} style={{position:"absolute",top:20,left:20,background:"none",border:"none",color:"#94A3B8",fontSize:13,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:4}}>
          ← Back
        </button>
      )}
      <div style={{textAlign:"center",marginBottom:28}}>
        <img src="/logo192.png" alt="SolFri" style={{width:72,height:72,marginBottom:8,borderRadius:16}}/>
        <h1 style={{fontFamily:"'Zen Old Mincho',Georgia,serif",fontSize:46,fontWeight:600,color:"#5C3D2E",margin:"0 0 10px",letterSpacing:"0.2em",lineHeight:1.1}}>SolFri</h1>
        <p style={{fontFamily:"'Zen Kaku Gothic New',sans-serif",fontSize:11,color:"#8F7866",fontWeight:400,letterSpacing:"0.4em",margin:"0 0 10px",textTransform:"lowercase"}}>set your soul free</p>
        <p style={{color:"#64748B",fontSize:14,margin:0}}>Your mental wellness companion</p>
      </div>

      <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:28,boxShadow:"0 1px 2px rgba(92,61,46,0.06), 0 5px 14px rgba(92,61,46,0.07), 0 18px 42px rgba(92,61,46,0.08)",width:"100%",maxWidth:400}}>
        <h2 style={{fontSize:18,fontWeight:800,color:"#1E293B",margin:"0 0 6px"}}>{mode==="login"?"Welcome back":"Create your account"}</h2>
        <p style={{fontSize:13,color:"#64748B",margin:"0 0 20px",lineHeight:1.6}}>{mode==="login"?"Sign in to access your history.":"Your data is saved privately and securely."}</p>

        {/* Google sign in */}
        <button onClick={handleGoogle} disabled={googleLoading} style={{width:"100%",background:"#fff",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 20px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:10,color:"#1E293B",marginBottom:16,boxShadow:"0 1px 2px rgba(92,61,46,0.05), 0 2px 6px rgba(92,61,46,0.05)"}}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.5 26.8 36 24 36c-5.2 0-9.6-3-11.3-7.2l-6.5 5C9.6 39.5 16.3 44 24 44z"/>
            <path fill="#1565C0" d="M43.6 20H24v8h11.3c-.9 2.5-2.6 4.6-4.9 6l6.2 5.2C40.2 35.7 44 30.3 44 24c0-1.3-.1-2.7-.4-4z"/>
          </svg>
          {googleLoading ? "Signing in…" : "Continue with Google"}
        </button>

        <button onClick={handleMicrosoft} disabled={microsoftLoading} style={{width:"100%",background:"#fff",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 20px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:10,color:"#1E293B",marginBottom:16,boxShadow:"0 1px 2px rgba(92,61,46,0.05), 0 2px 6px rgba(92,61,46,0.05)"}}>
          <svg width="18" height="18" viewBox="0 0 24 24"><rect x="1" y="1" width="10" height="10" fill="#F25022"/><rect x="13" y="1" width="10" height="10" fill="#7FBA00"/><rect x="1" y="13" width="10" height="10" fill="#00A4EF"/><rect x="13" y="13" width="10" height="10" fill="#FFB900"/></svg>
          {microsoftLoading ? "Signing in…" : "Continue with Microsoft"}
        </button>

        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
          <span style={{fontSize:12,color:"#94A3B8",fontWeight:500}}>or use email</span>
          <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
        </div>

        {magicLinkSent ? (
          <div style={{textAlign:"center",padding:"12px 0"}}>
            <p style={{fontSize:32,marginBottom:10}}>✨</p>
            <p style={{fontSize:15,fontWeight:700,color:"#1E293B",marginBottom:8}}>Check your email</p>
            <p style={{fontSize:13,color:"#64748B",lineHeight:1.6,marginBottom:16}}>We sent a sign-in link to <strong>{email}</strong>. Open it on this device to sign in — you can close this tab.</p>
            <button onClick={()=>{setMagicLinkSent(false);setMode("login");}} style={{background:"none",border:"none",color:"#6366F1",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>← Back</button>
          </div>
        ) : mode==="magiclink" ? (
          <>
            <p style={{fontSize:13,color:"#64748B",lineHeight:1.6,marginBottom:14}}>We'll email you a link — no password needed. Works with any email address, including Apple/iCloud ones.</p>
            {inp("Email address", email, setEmail, "email")}
            {error && <p style={{color:"#EF4444",fontSize:13,marginBottom:12,lineHeight:1.5}}>⚠️ {error}</p>}
            <button onClick={sendMagicLink} disabled={loading} style={{width:"100%",background:loading?"#CBD5E1":"#6366F1",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",boxShadow:loading?"none":"0 1px 2px rgba(40,40,90,0.14), 0 4px 12px rgba(99,102,241,0.30), 0 10px 24px rgba(99,102,241,0.18)",fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:14}}>
              {loading?"Sending…":"Send me a magic link ✨"}
            </button>
            <p style={{textAlign:"center",fontSize:13,color:"#64748B",margin:0}}>
              <button onClick={()=>{setMode("login");setError(null);}} style={{background:"none",border:"none",color:"#6366F1",fontWeight:700,cursor:"pointer",fontFamily:"inherit",fontSize:13,padding:0}}>← Use a password instead</button>
            </p>
          </>
        ) : (
          <>
            {mode==="register" && inp("Your first name", name, setName)}
            {mode==="register" && (
              <div style={{marginBottom:10}}>
                <label style={{fontSize:12,color:"#64748B",display:"block",marginBottom:5}}>Date of birth</label>
                <input type="date" value={dob} onChange={e=>setDob(e.target.value)} max={new Date().toISOString().split("T")[0]}
                  style={{width:"100%",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 15px",fontSize:15,color:"#1E293B",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#fff"}}
                  onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}
                />
                <p style={{fontSize:11,color:"#94A3B8",margin:"5px 0 0"}}>You must be 16 or older to use SolFri.</p>
              </div>
            )}
            {inp("Email address", email, setEmail, "email")}
            {inp("Password (min 6 characters)", password, setPassword, "password")}

            {error && <p style={{color:"#EF4444",fontSize:13,marginBottom:12,lineHeight:1.5}}>⚠️ {error}</p>}

            <button onClick={handleEmailAuth} disabled={loading} style={{width:"100%",background:loading?"#CBD5E1":"#6366F1",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",boxShadow:loading?"none":"0 1px 2px rgba(40,40,90,0.14), 0 4px 12px rgba(99,102,241,0.30), 0 10px 24px rgba(99,102,241,0.18)",fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:14}}>
              {loading?(mode==="login"?"Signing in…":"Creating account…"):(mode==="login"?"Sign in →":"Create account →")}
            </button>

            <p style={{textAlign:"center",fontSize:13,color:"#64748B",margin:"0 0 10px"}}>
              {mode==="login"?"Don't have an account? ":"Already have an account? "}
              <button onClick={()=>{setMode(mode==="login"?"register":"login");setError(null);}} style={{background:"none",border:"none",color:"#6366F1",fontWeight:700,cursor:"pointer",fontFamily:"inherit",fontSize:13,padding:0}}>
                {mode==="login"?"Create one":"Sign in"}
              </button>
            </p>
            <p style={{textAlign:"center",fontSize:13,margin:0}}>
              <button onClick={()=>{setMode("magiclink");setError(null);}} style={{background:"none",border:"none",color:"#6366F1",fontWeight:700,cursor:"pointer",fontFamily:"inherit",fontSize:13,padding:0}}>✨ Sign in with a magic link instead</button>
            </p>
          </>
        )}
      </div>

      <p style={{color:"#94A3B8",fontSize:11,textAlign:"center",marginTop:20,lineHeight:1.6}}>
        In crisis? Please contact your local emergency services or a crisis helpline in your area.
      </p>
      <p style={{textAlign:"center",fontSize:10.5,marginTop:8}}>
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{color:"#7A6046",textDecoration:"underline"}}>Privacy Policy</a>
        <span style={{color:"#E8D5C4",margin:"0 8px"}}>·</span>
        <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{color:"#7A6046",textDecoration:"underline"}}>Terms of Service</a>
      </p>
    </div>
  );
}

// ─── Response card ────────────────────────────────────────────────────────────
function ResponseCard({ response, onContinue, onEndSession, exchangeCount, exchangeLimit, input, setInput, loading, userIntent, setUserIntent, language }) {
  const [showBreath, setShowBreath] = useState(false);
  const t = theme(response.emotion);
  const remaining = exchangeLimit - exchangeCount;
  return (
    <>
      <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:26,boxShadow:"0 1px 2px rgba(92,61,46,0.05), 0 5px 14px rgba(92,61,46,0.06), 0 18px 42px rgba(92,61,46,0.07)",border:`1px solid ${t.light}`,marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
          <span style={{fontSize:24}}>{t.emoji}</span>
          <span style={{background:t.light,color:t.accent,borderRadius:20,padding:"4px 14px",fontSize:13,fontWeight:700,textTransform:"capitalize"}}>{response.emotion}</span>
          {response.response_mode==="listen" && (
            <span style={{background:"#FDF0E6",color:"#C4856A",borderRadius:20,padding:"4px 12px",fontSize:11,fontWeight:700}}>💛 listening</span>
          )}
          <span style={{marginLeft:"auto",fontSize:11,color:"#94A3B8"}}>{exchangeCount}/{exchangeLimit}</span>
        </div>
        <p style={{fontSize:16,lineHeight:1.65,fontWeight:500,color:"#1E293B",marginBottom:10}}>{response.empathy}</p>
        <p style={{fontSize:14,color:"#64748B",lineHeight:1.6,marginBottom:response.pattern?14:18}}>{response.validation}</p>
        {response.pattern && (
          <div style={{background:t.bg,borderRadius:12,padding:"10px 14px",marginBottom:18,border:`1px solid ${t.light}`}}>
            <p style={{fontSize:13,color:t.accent,fontWeight:600,margin:0}}>🔍 {response.pattern}</p>
          </div>
        )}
        {response.gentle_truth && (
          <div style={{background:"#FFFBEB",borderRadius:12,padding:"12px 14px",marginBottom:18,border:"1px solid #FDE68A"}}>
            <p style={{fontSize:12,color:"#92400E",fontWeight:700,margin:"0 0 4px",letterSpacing:"0.5px",textTransform:"uppercase"}}>A gentle observation</p>
            <p style={{fontSize:13,color:"#78350F",fontWeight:500,margin:0,lineHeight:1.6}}>{response.gentle_truth}</p>
          </div>
        )}
        {response.crisis && (
          <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:12,padding:"12px 16px",marginBottom:18}}>
            <p style={{color:"#DC2626",fontSize:13,lineHeight:1.6,margin:0}}>🆘 {response.crisis}</p>
          </div>
        )}
        {response.milestone_progress && (
          <div style={{background:"#F0F7F1",borderRadius:12,padding:"12px 14px",marginBottom:18,border:"1px solid #B8D4B0"}}>
            <p style={{fontSize:11,color:"#5C8B60",fontWeight:700,margin:"0 0 4px",letterSpacing:"0.5px",textTransform:"uppercase"}}>
              {response.milestone_progress.status === "completed" ? "🌿 Milestone complete" : response.milestone_progress.status === "progressing" ? "🌱 Progress on your milestone" : "🧭 Your milestone"}
            </p>
            <p style={{fontSize:13,color:"#3F6B44",fontWeight:500,margin:0,lineHeight:1.6}}>{response.milestone_progress.note}</p>
          </div>
        )}
        {response.strategies?.length > 0 && (<>
        <p style={{color:"#94A3B8",fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12}}>What might help</p>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:18}}>
          {response.strategies?.map((s,i)=>(
            <div key={i} style={{background:t.bg,borderRadius:14,padding:"13px 15px",border:`1px solid ${t.light}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                <span style={{fontSize:17}}>{s.icon||"✨"}</span>
                <span style={{fontWeight:700,fontSize:14,color:"#1E293B"}}>{s.title}</span>
              </div>
              <p style={{color:"#475569",fontSize:13,lineHeight:1.6,margin:0}}>{s.description}</p>
            </div>
          ))}
        </div>
        </>)}
        <div style={{borderTop:`1px solid ${t.light}`,paddingTop:14,marginBottom:14}}>
          <button onClick={()=>setShowBreath(v=>!v)} style={{background:"none",border:"none",color:t.accent,fontSize:13,fontWeight:600,cursor:"pointer",padding:0}}>
            {showBreath?"▲ Hide breathing exercise":"🌬️ Open guided breathing exercise"}
          </button>
          {showBreath && <Breathing accent={t.accent}/>}
        </div>
        {response.invitation && (
          <div style={{background:"#FDF8F3",borderLeft:"3px solid #C4856A",borderRadius:"0 10px 10px 0",padding:"11px 14px",marginBottom:14}}>
            <p style={{fontSize:14,color:"#5C3D2E",fontStyle:"italic",margin:0,lineHeight:1.65}}>{response.invitation}</p>
          </div>
        )}
        <p style={{color:"#475569",fontSize:14,lineHeight:1.65,fontStyle:"italic",marginBottom:18}}>{response.closing}</p>
        <button onClick={onEndSession} style={{width:"100%",background:"transparent",border:"1px solid #E2E8F0",color:"#64748B",borderRadius:12,padding:"11px 0",fontSize:13,cursor:"pointer",fontWeight:600,fontFamily:"inherit"}}>End session</button>
      </div>
      {remaining>0
        ? <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:16,padding:16,boxShadow:"0 1px 2px rgba(92,61,46,0.045), 0 3px 8px rgba(92,61,46,0.05), 0 10px 24px rgba(92,61,46,0.055)",marginBottom:16}}>
            <p style={{fontSize:11,color:"#94A3B8",margin:"0 0 10px",fontWeight:600}}>{remaining} exchange{remaining!==1?"s":""} remaining this session</p>
            <div style={{position:"relative"}}>
              <textarea value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();onContinue();}}} placeholder="Continue sharing — type or tap 🎤…" rows={2}
                style={{width:"100%",border:"1.5px solid #E2E8F0",borderRadius:10,padding:"10px 13px",paddingRight:48,fontSize:14,lineHeight:1.6,color:"#1E293B",resize:"none",outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}
                onFocus={e=>e.target.style.borderColor=t.accent} onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
              <VoiceMic value={input} setValue={setInput} language={language} style={{top:7,right:7,width:30,height:30,fontSize:14}}/>
            </div>
            {input.trim() && (
              <div style={{marginTop:10}}>
                <p style={{fontSize:11,color:"#94A3B8",margin:"0 0 7px",fontWeight:600}}>What would help most?</p>
                <IntentChips value={userIntent} onChange={setUserIntent} compact/>
              </div>
            )}
            <button onClick={onContinue} disabled={loading||!input.trim()} style={{marginTop:10,width:"100%",background:loading||!input.trim()?"#CBD5E1":t.accent,color:"#fff",border:"none",borderRadius:10,padding:"12px 0",fontSize:14,fontWeight:700,cursor:loading||!input.trim()?"not-allowed":"pointer",fontFamily:"inherit"}}>
              {loading?"Listening…":"Continue →"}
            </button>
          </div>
        : <div style={{background:"#FEF9C3",border:"1px solid #FDE68A",borderRadius:14,padding:"14px 16px",marginBottom:16,textAlign:"center"}}>
            <p style={{fontSize:13,color:"#92400E",fontWeight:600,margin:0}}>You've reached the {exchangeLimit}-message limit for this session. {exchangeLimit<30 && "Upgrade to SolFri Plus for longer sessions, or "}end and start a new one.</p>
          </div>
      }
    </>
  );
}


// ─── SolFri is listening overlay ────────────────────────────────────────────
// ─── Ensō ──────────────────────────────────────────────────────────────────
// The zen circle, traditionally painted in a single breath and left open.
// It's the one bold element on the page: it frames the bonsai, and it draws
// itself once on arrival — a moment of stillness before anything is asked of
// the person. Left deliberately open at the top right, as an ensō always is:
// the gap is the point, not a flaw. Chosen over a decorative ring because it
// comes from the bonsai's own world rather than being applied to it.
function Enso({ size = 300 }) {
  const r = 42;
  const circumference = 2 * Math.PI * r;
  const gap = circumference * 0.16;      // the opening
  const drawn = circumference - gap;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true"
      style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-52%) rotate(-38deg)",pointerEvents:"none",zIndex:0}}>
      <defs>
        {/* Ink is denser where the brush begins and thins as it lifts */}
        <linearGradient id="ensoInk" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#8A6A52" stopOpacity="0.42"/>
          <stop offset="45%"  stopColor="#A0673A" stopOpacity="0.30"/>
          <stop offset="100%" stopColor="#C4856A" stopOpacity="0.10"/>
        </linearGradient>
      </defs>
      <circle className="enso-stroke" cx="50" cy="50" r={r}
        fill="none" stroke="url(#ensoInk)" strokeWidth="3.2" strokeLinecap="round"
        strokeDasharray={`${drawn} ${gap}`}
        style={{ "--enso-len": circumference }}/>
      {/* A second, lighter pass — real brushwork is never one flat weight */}
      <circle className="enso-stroke enso-stroke-2" cx="50" cy="50" r={r}
        fill="none" stroke="url(#ensoInk)" strokeWidth="1.1" strokeLinecap="round"
        strokeDasharray={`${drawn * 0.82} ${circumference}`}
        style={{ "--enso-len": circumference }}/>
    </svg>
  );
}

function IntentChips({ value, onChange, compact }) {
  const opts = [
    { k: "listen", l: "💛 Just listen" },
    { k: "guide",  l: compact ? "🌿 Take action" : "🌿 Help me take action" },
    { k: "auto",   l: compact ? "✨ You choose" : "✨ Choose for me" },
  ];
  return (
    <div style={{display:"flex",gap:8,marginBottom:12}}>
      {opts.map(o => (
        <button key={o.k} onClick={()=>onChange(o.k)}
          style={{flex:1,background:value===o.k?"#5C3D2E":"#fff",color:value===o.k?"#fff":"#A08070",border:`1.5px solid ${value===o.k?"#5C3D2E":"#E8D5C4"}`,borderRadius:20,padding:compact?"6px 4px":"9px 4px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function ListeningOverlay() {
  const [dots, setDots] = useState("");
  const [msgIdx, setMsgIdx] = useState(0);
  const messages = [
    "SolFri is listening",
    "Taking in every word",
    "Thinking about what you shared",
    "Preparing a thoughtful response",
  ];
  useEffect(() => {
    const dotTimer = setInterval(() => setDots(d => d.length >= 3 ? "" : d + "."), 450);
    const msgTimer = setInterval(() => setMsgIdx(i => (i + 1) % messages.length), 3200);
    return () => { clearInterval(dotTimer); clearInterval(msgTimer); };
  }, []);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(253,248,243,0.96)",zIndex:300,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{position:"relative",width:110,height:110,marginBottom:24}}>
        <div style={{position:"absolute",inset:0,borderRadius:"50%",border:"3px solid #E8D5C4",animation:"solfriPulse 2s ease-in-out infinite"}}/>
        <div style={{position:"absolute",inset:12,borderRadius:"50%",border:"3px solid #C4856A",animation:"solfriPulse 2s ease-in-out infinite 0.3s"}}/>
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:44}}>{"🌳"}</div>
      </div>
      <p style={{fontSize:17,fontWeight:700,color:"#5C3D2E",margin:"0 0 6px",minHeight:24}}>{messages[msgIdx]}{dots}</p>
      <p style={{fontSize:13,color:"#A08070",margin:0,fontStyle:"italic"}}>Take a slow breath while you wait</p>
      <style>{`@keyframes solfriPulse { 0%,100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.12); opacity: 1; } }`}</style>
    </div>
  );
}

// ─── Age confirmation gate — shown to ANY signed-in user with no dob on file ──
// Catches Google sign-ins (which skip the registration form entirely) and any
// account created before this check existed.
function AgeGate({ onConfirm, onSignOut }) {
  const [dob, setDob] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!dob) { setError("Please enter your date of birth."); return; }
    setLoading(true); setError(null);
    const result = await onConfirm(dob);
    if (!result.ok) {
      setError("You need to be at least 16 to use SolFri. If you're going through a hard time, please reach out to a parent, trusted adult, or a local support service — they're there for you too.");
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#F8FAFC",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 16px",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:28,boxShadow:"0 1px 2px rgba(92,61,46,0.06), 0 5px 14px rgba(92,61,46,0.07), 0 18px 42px rgba(92,61,46,0.08)",width:"100%",maxWidth:400}}>
        <div style={{fontSize:40,textAlign:"center",marginBottom:10}}>🌳</div>
        <h2 style={{fontSize:18,fontWeight:800,color:"#1E293B",margin:"0 0 6px",textAlign:"center"}}>One quick thing</h2>
        <p style={{fontSize:13,color:"#64748B",margin:"0 0 20px",lineHeight:1.6,textAlign:"center"}}>Before you continue, please confirm your date of birth. SolFri is for people 16 and older.</p>
        <label style={{fontSize:12,color:"#64748B",display:"block",marginBottom:5}}>Date of birth</label>
        <input type="date" value={dob} onChange={e=>setDob(e.target.value)} max={new Date().toISOString().split("T")[0]}
          style={{width:"100%",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 15px",fontSize:15,color:"#1E293B",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#fff",marginBottom:14}}
          onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}
        />
        {error && <p style={{color:"#EF4444",fontSize:13,marginBottom:14,lineHeight:1.5}}>⚠️ {error}</p>}
        <button onClick={submit} disabled={loading} style={{width:"100%",background:loading?"#CBD5E1":"#6366F1",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",boxShadow:loading?"none":"0 1px 2px rgba(40,40,90,0.14), 0 4px 12px rgba(99,102,241,0.30), 0 10px 24px rgba(99,102,241,0.18)",fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:10}}>
          {loading?"Confirming…":"Continue →"}
        </button>
        <button onClick={onSignOut} style={{width:"100%",background:"none",border:"none",color:"#94A3B8",fontSize:13,cursor:"pointer",fontFamily:"inherit",padding:6}}>
          Sign out instead
        </button>
      </div>
    </div>
  );
}

// ─── Privacy Passphrase gate — separate from login, never sent to our servers ──
// "create" mode: first time, no encryption set up yet. "unlock" mode: returning
// on a new device/session, need the passphrase again to re-derive the key.
function PassphraseGate({ mode, onSubmit, onBackupCodeSubmit, onBackupCodeAcknowledged, onSignOut }) {
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryError, setRecoveryError] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [justCreatedBackupCode, setJustCreatedBackupCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const submit = async () => {
    setError(null);
    if (!pass || pass.length < 8) { setError("Please use at least 8 characters."); return; }
    if (mode === "create" && pass !== confirm) { setError("The two entries don't match."); return; }
    setLoading(true);
    const result = await onSubmit(pass);
    if (!result.ok) { setError(result.error || "Incorrect passphrase."); setLoading(false); return; }
    if (mode === "create" && result.backupCode) {
      // Show the backup code once, before letting them into the app — this
      // is the one and only time it will ever be displayed.
      setJustCreatedBackupCode(result.backupCode);
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const submitRecovery = async () => {
    setRecoveryError(null);
    if (!recoveryCode.trim()) { setRecoveryError("Please enter your backup code."); return; }
    setRecoveryLoading(true);
    const result = await onBackupCodeSubmit(recoveryCode);
    if (!result.ok) { setRecoveryError(result.error || "That code didn't work."); setRecoveryLoading(false); }
  };

  const inputStyle = {width:"100%",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"13px 15px",fontSize:15,color:"#1E293B",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#fff",marginBottom:10};

  // ── The one-time "save your backup code now" screen, shown right after
  // creating a passphrase — this takes over the whole gate until dismissed.
  if (justCreatedBackupCode) {
    return (
      <div style={{minHeight:"100vh",background:"#F8FAFC",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 16px",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
        <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:28,boxShadow:"0 1px 2px rgba(92,61,46,0.06), 0 5px 14px rgba(92,61,46,0.07), 0 18px 42px rgba(92,61,46,0.08)",width:"100%",maxWidth:400,textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:10}}>🔑</div>
          <h2 style={{fontSize:18,fontWeight:800,color:"#1E293B",margin:"0 0 8px"}}>Save your backup code</h2>
          <p style={{fontSize:13,color:"#64748B",margin:"0 0 16px",lineHeight:1.65}}>
            If you ever forget your passphrase, this code is the <strong>only</strong> other way back into your encrypted sessions. We're showing it to you <strong>once, right now</strong> — we don't store it anywhere we could show it again.
          </p>
          <div style={{background:"#F8FAFC",border:"1.5px dashed #CBD5E1",borderRadius:12,padding:"16px 10px",marginBottom:16}}>
            <p style={{fontSize:17,fontWeight:800,letterSpacing:"1.5px",color:"#1E293B",margin:0,fontFamily:"monospace",wordBreak:"break-all"}}>{justCreatedBackupCode}</p>
          </div>
          <button onClick={()=>{navigator.clipboard?.writeText(justCreatedBackupCode);setCopied(true);}} style={{width:"100%",background:copied?"#22C55E":"#6366F1",color:"#fff",border:"none",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10}}>
            {copied ? "✓ Copied" : "Copy code"}
          </button>
          <p style={{fontSize:11.5,color:"#94A3B8",lineHeight:1.6,marginBottom:16}}>Store it somewhere safe — a password manager, or written down somewhere only you can access. Anyone with this code can unlock your sessions, same as your passphrase.</p>
          <button onClick={()=>{setJustCreatedBackupCode(null);onBackupCodeAcknowledged();}} style={{width:"100%",background:"none",border:"1.5px solid #E2E8F0",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:"#1E293B"}}>
            I've saved it — continue →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#F8FAFC",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 16px",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:28,boxShadow:"0 1px 2px rgba(92,61,46,0.06), 0 5px 14px rgba(92,61,46,0.07), 0 18px 42px rgba(92,61,46,0.08)",width:"100%",maxWidth:400}}>
        <div style={{fontSize:40,textAlign:"center",marginBottom:10}}>🔒</div>
        <h2 style={{fontSize:18,fontWeight:800,color:"#1E293B",margin:"0 0 6px",textAlign:"center"}}>
          {mode==="create" ? "Create your Privacy Passphrase" : "Unlock your reflections"}
        </h2>
        <p style={{fontSize:13,color:"#64748B",margin:"0 0 18px",lineHeight:1.6,textAlign:"center"}}>
          {mode==="create"
            ? "This is separate from your login password. It encrypts your sessions so that only you — not even us — can read them."
            : "Enter your Privacy Passphrase to decrypt your sessions on this device."}
        </p>

        {mode==="create" && (
          <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:"11px 14px",marginBottom:16}}>
            <p style={{fontSize:12,color:"#92400E",margin:0,lineHeight:1.6}}>
              <strong>We cannot reset this.</strong> If you forget it, you'll need the backup code we'll show you next — please store both somewhere safe, like a password manager.
            </p>
          </div>
        )}

        {mode === "unlock" && showForgot ? (
          <>
            <p style={{fontSize:12.5,color:"#64748B",textAlign:"center",lineHeight:1.6,marginBottom:12}}>
              Enter the backup code you saved when you first created your passphrase.
            </p>
            <input type="text" placeholder="XXXXX-XXXXX-XXXXX-XXXXX" value={recoveryCode} onChange={e=>setRecoveryCode(e.target.value.toUpperCase())}
              onKeyDown={e=>{if(e.key==="Enter")submitRecovery();}}
              style={{...inputStyle, textAlign:"center", fontFamily:"monospace", letterSpacing:"1px"}}
              onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
            {recoveryError && <p style={{color:"#EF4444",fontSize:13,marginBottom:12,lineHeight:1.5}}>⚠️ {recoveryError}</p>}
            <button onClick={submitRecovery} disabled={recoveryLoading} style={{width:"100%",background:recoveryLoading?"#CBD5E1":"#6366F1",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",fontSize:15,fontWeight:700,cursor:recoveryLoading?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:10}}>
              {recoveryLoading ? "Checking…" : "Unlock with backup code →"}
            </button>
            <button onClick={()=>{setShowForgot(false);setRecoveryError(null);setRecoveryCode("");}} style={{width:"100%",background:"none",border:"none",color:"#94A3B8",fontSize:12,cursor:"pointer",fontFamily:"inherit",padding:6}}>
              ← Back to passphrase
            </button>
            <p style={{fontSize:11,color:"#94A3B8",textAlign:"center",lineHeight:1.6,marginTop:10}}>
              Don't have your backup code either? Sessions encrypted before today's update won't have one — in that case, we genuinely cannot recover them. You can still sign out and start fresh.
            </p>
          </>
        ) : (
          <>
            <div style={{position:"relative"}}>
              <input type={showPass?"text":"password"} placeholder="Privacy Passphrase (min. 8 characters)" value={pass} onChange={e=>setPass(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&mode==="unlock")submit();}}
                style={{...inputStyle,paddingRight:42}} onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
              <button type="button" onClick={()=>setShowPass(v=>!v)} aria-label={showPass?"Hide passphrase":"Show passphrase"}
                style={{position:"absolute",right:12,top:13,background:"none",border:"none",cursor:"pointer",fontSize:16,padding:0,color:"#94A3B8"}}>
                {showPass?"🙈":"👁️"}
              </button>
            </div>

            {mode==="create" && (
              <div style={{position:"relative"}}>
                <input type={showConfirm?"text":"password"} placeholder="Confirm passphrase" value={confirm} onChange={e=>setConfirm(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter")submit();}}
                  style={{...inputStyle,paddingRight:42}} onFocus={e=>e.target.style.borderColor="#6366F1"} onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
                <button type="button" onClick={()=>setShowConfirm(v=>!v)} aria-label={showConfirm?"Hide passphrase":"Show passphrase"}
                  style={{position:"absolute",right:12,top:13,background:"none",border:"none",cursor:"pointer",fontSize:16,padding:0,color:"#94A3B8"}}>
                  {showConfirm?"🙈":"👁️"}
                </button>
              </div>
            )}

            {error && <p style={{color:"#EF4444",fontSize:13,marginBottom:12,lineHeight:1.5}}>⚠️ {error}</p>}

            <button onClick={submit} disabled={loading} style={{width:"100%",background:loading?"#CBD5E1":"#6366F1",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",boxShadow:loading?"none":"0 1px 2px rgba(40,40,90,0.14), 0 4px 12px rgba(99,102,241,0.30), 0 10px 24px rgba(99,102,241,0.18)",fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:10}}>
              {loading ? "Working…" : mode==="create" ? "Create & continue →" : "Unlock →"}
            </button>

            {mode==="unlock" && (
              <button onClick={()=>setShowForgot(true)} style={{width:"100%",background:"none",border:"none",color:"#94A3B8",fontSize:12,cursor:"pointer",fontFamily:"inherit",padding:6}}>
                Forgot your passphrase? Use your backup code
              </button>
            )}
          </>
        )}
        <button onClick={onSignOut} style={{width:"100%",background:"none",border:"none",color:"#94A3B8",fontSize:13,cursor:"pointer",fontFamily:"inherit",padding:6,marginTop:4}}>
          Sign out instead
        </button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
// ─── Responsive container sizing ───────────────────────────────────────────
// Inline styles can't use media queries, so container widths are computed
// from live viewport width instead. Global scaling (TV zoom, safe areas,
// touch targets) is handled in responsive.css — this covers only what the
// inline style objects themselves need to read.
function useViewport() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    let frame = null;
    const onResize = () => {
      // rAF-throttled: resize fires continuously while dragging a window
      if (frame) return;
      frame = requestAnimationFrame(() => { setWidth(window.innerWidth); frame = null; });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Reading-comfort widths: a single column of text stays readable up to
  // roughly 900px, so these widen with the screen but never sprawl.
  const contentWidth = width >= 1600 ? 900 : width >= 1100 ? 760 : 560;
  return { width, contentWidth, isNarrow: width < 480 };
}

// Single words rather than sentence fragments — quicker to scan, and they
// don't put words in someone's mouth about *why* they feel it. Tapping one
// seeds the box; they can then say as much or as little as they want.
// Positive emotions are deliberately included and visually distinct: SolFri
// isn't only for hard days, and someone arriving to say they feel good
// shouldn't have to scroll past a wall of difficult words to find themselves.
const STARTERS = [
  { w: "Anxious",     tone: "hard", featured: true },
  { w: "Sad",         tone: "hard", featured: true },
  { w: "Overwhelmed", tone: "hard", featured: true },
  { w: "Lonely",      tone: "hard", featured: true },
  { w: "Angry",       tone: "hard" },
  { w: "Stressed",    tone: "hard" },
  { w: "Exhausted",   tone: "hard" },
  { w: "Numb",        tone: "hard" },
  { w: "Afraid",      tone: "hard" },
  { w: "Guilty",      tone: "hard" },
  { w: "Hopeful",     tone: "good", featured: true },
  { w: "Calm",        tone: "good", featured: true },
  { w: "Grateful",    tone: "good", featured: true },
  { w: "Proud",       tone: "good" },
  { w: "Content",     tone: "good" },
  { w: "Excited",     tone: "good" },
  { w: "Relieved",    tone: "good" },
  { w: "Loved",       tone: "good" },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [allSessions, setAllSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [apiHistory, setApiHistory] = useState([]);
  const [latestResponse, setLatestResponse] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState("input");
  const [showHistory, setShowHistory] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem("solfri_lang") || "en");
  const [showLangSelector, setShowLangSelector] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const { contentWidth, isNarrow } = useViewport();

  // Bumping this remounts the bonsai wrapper, replaying its CSS rustle
  // animation. Starts at 1 so the tree stirs on first render after sign-in,
  // then fires again on every message sent.
  const [bonsaiRustle, setBonsaiRustle] = useState(1);
  const [showAllEmotions, setShowAllEmotions] = useState(false);


  const rustleBonsai = () => setBonsaiRustle(n => n + 1);

  const [showCrisisPanel, setShowCrisisPanel] = useState(false);
  const [locationInfo, setLocationInfo] = useState({ countryCode: null, countryName: null });
  // Never null: the crisis Help button must exist from first paint, before
  // location detection finishes (a network call) and even if it fails
  // outright. Upgrades to country-specific resources once detection lands.
  const [crisisResources, setCrisisResources] = useState(INTERNATIONAL_RESOURCES);
  const [journey, setJourney] = useState(null);
  const [showJourney, setShowJourney] = useState(false);
  const [journeyCreating, setJourneyCreating] = useState(false);
  const [journeyError, setJourneyError] = useState(null);
  const [sessionMode, setSessionMode] = useState("open");
  const [userIntent, setUserIntent] = useState("auto");
  const [ageStatus, setAgeStatus] = useState("checking"); // checking | needed | confirmed
  const [encKey, setEncKey] = useState(null);
  const [encStatus, setEncStatus] = useState("checking"); // checking | needed_create | needed_unlock | ready
  const [tier, setTier] = useState("free");
  const [sessionsThisMonth, setSessionsThisMonth] = useState(0);
  const [isIndividualSubscriber, setIsIndividualSubscriber] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [showVoucherInput, setShowVoucherInput] = useState(false);
  const [showTeamPurchase, setShowTeamPurchase] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState(null);
  const [showTeamInput, setShowTeamInput] = useState(false);
  const [teamCode, setTeamCode] = useState("");
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [teamJoinedName, setTeamJoinedName] = useState(null);

  const joinTeam = async () => {
    if (!teamCode.trim()) { setTeamError("Please enter a team code."); return; }
    setTeamLoading(true); setTeamError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/join-team", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ code: teamCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't join that team.");
      setTier("plus");
      setTeamJoinedName(data.companyName || "your team");
      setShowTeamInput(false);
      setTeamCode("");
    } catch (e) {
      setTeamError(e.message);
    } finally {
      setTeamLoading(false);
    }
  };

  const redeemVoucher = async () => {
    if (!voucherCode.trim()) { setVoucherError("Please enter a code."); return; }
    setVoucherLoading(true); setVoucherError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/redeem-voucher", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ code: voucherCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't redeem that code.");
      setTier("plus");
      setShowVoucherInput(false);
      setVoucherCode("");
    } catch (e) {
      setVoucherError(e.message);
    } finally {
      setVoucherLoading(false);
    }
  };

  // Paystack LIVE public key — safe to expose client-side, same as Firebase's config
  // Safe to expose client-side — Paystack's public key is designed to be public,
  // same principle as Firebase's client config. The secret key never goes here.
  const PAYSTACK_PUBLIC_KEY = "pk_live_572ffd5d58f01e255bbfa45a5eff2e94e771024d";
  // ⚠️ SET THIS to the Plan Code from Paystack → Products → Plans (looks like PLN_xxxxx)
  const PAYSTACK_PLAN_CODE = "PLN_chz1umebl0fw71e";

  const startUpgrade = () => {
    if (!window.PaystackPop) {
      setUpgradeError("Payment system is still loading — please try again in a moment.");
      return;
    }
    setUpgradeError(null);
    setUpgradeLoading(true);
    try {
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: user.email,
        amount: 5500, // R55.00 in cents — Paystack's own docs say this is required even
                      // when a plan is set; the plan's own price takes precedence anyway
        currency: "ZAR",
        plan: PAYSTACK_PLAN_CODE,
        ref: `solfri_${user.uid.slice(0, 8)}_${Date.now()}`,
        metadata: { uid: user.uid },
        callback: (response) => { verifyPayment(response.reference); },
        onClose: () => { setUpgradeLoading(false); },
      });
      if (!handler) throw new Error("Paystack did not return a checkout handler");
      handler.openIframe();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Paystack setup failed:", e);
      setUpgradeError(`Couldn't open checkout (${e.message || "unknown error"}). Please try again.`);
      setUpgradeLoading(false);
    }
  };

  const verifyPayment = async (reference) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/paystack-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ reference }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Payment could not be verified. Please contact support if you were charged.");
      setTier("plus");
      setIsIndividualSubscriber(true); // this WAS a genuine individual Paystack payment — reflect it immediately, don't wait for a reload
      setIsCancelled(false); // covers resubscribing after a previous cancellation too
      setUpgradeError(null);
    } catch (e) {
      setUpgradeError(e.message);
    } finally {
      setUpgradeLoading(false);
    }
  };
  const [usageLoaded, setUsageLoaded] = useState(false);

  const LIMITS = { free: { sessionsPerMonth: 3, exchangesPerSession: 5 }, plus: { sessionsPerMonth: Infinity, exchangesPerSession: 30 } };
  const exchangeLimit = LIMITS[tier].exchangesPerSession;
  const sessionLimit = LIMITS[tier].sessionsPerMonth;
  const monthKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; };

  // Load subscription tier + this month's usage — same source of truth the
  // server checks, so the UI never promises something the API won't allow.
  useEffect(() => {
    if (!user || ageStatus !== "confirmed") return;
    setUsageLoaded(false);
    Promise.all([
      getDoc(doc(db, "users", user.uid, "meta", "subscription")).catch(() => null),
      getDoc(doc(db, "users", user.uid, "meta", "usage")).catch(() => null),
    ]).then(([subSnap, usageSnap]) => {
      const subData = subSnap && subSnap.exists() ? subSnap.data() : null;
      setTier(subData && subData.tier === "plus" ? "plus" : "free");
      // Only a genuine individual Paystack subscriber can cancel here — not a
      // team member (their access comes from the team, not personal billing)
      // and not someone already cancelled (nothing left to cancel).
      setIsIndividualSubscriber(!!(subData && subData.paystackSubscriptionCode && !subData.teamId));
      setIsCancelled(!!(subData && subData.cancelledAt));
      const u = usageSnap && usageSnap.exists() ? usageSnap.data() : null;
      setSessionsThisMonth(u && u.month === monthKey() ? (u.sessionsThisMonth || 0) : 0);
      setUsageLoaded(true);
    });
  }, [user?.uid, ageStatus]);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setAuthReady(true); });
    return unsub;
  }, []);

  // Complete a magic-link sign-in if this page load came from clicking the
  // emailed link. Runs once, on mount, before anything else needs to know.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    let email = window.localStorage.getItem("solfri_magiclink_email");
    if (!email) {
      // Link opened on a different device/browser than it was requested from
      // — the safest fallback is asking, since we have no other way to know.
      email = window.prompt("Please confirm the email address this link was sent to:");
    }
    if (!email) return;
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem("solfri_magiclink_email");
        // Clean the sign-in params out of the address bar so the link can't
        // be accidentally reused or left sitting in browser history.
        window.history.replaceState({}, document.title, window.location.pathname);
      })
      .catch((e) => {
        window.localStorage.removeItem("solfri_magiclink_email");
        const code = e.code || "";
        const msg = code === "auth/invalid-action-code"
          ? "This magic link has expired or was already used — please request a new one."
          : code === "auth/invalid-email"
          ? "That email doesn't match the one this link was sent to — please request a new link."
          : `Sign-in link failed (${code || e.message || "unknown error"}) — please request a new one.`;
        window.alert(msg);
        window.history.replaceState({}, document.title, window.location.pathname);
      });
  }, []);

  // Age confirmation — runs for EVERY signed-in user, however they signed in
  // (email/password, Google, or an account created before this check existed).
  // This is the actual enforcement; the sign-up form is just the friendly path.
  useEffect(() => {
    if (!user) { setAgeStatus("checking"); return; }
    setAgeStatus("checking");
    getDoc(doc(db, "users", user.uid, "meta", "account"))
      .then(snap => {
        setAgeStatus(snap.exists() && snap.data().dob ? "confirmed" : "needed");
        // Account-level language preference follows the person across devices —
        // takes over from whatever localStorage had as the fast initial default.
        const savedLang = snap.exists() ? snap.data().language : null;
        if (savedLang) {
          setLanguage(savedLang);
          localStorage.setItem("solfri_lang", savedLang);
        }
      })
      .catch(() => setAgeStatus("needed"));
  }, [user?.uid]);

  const confirmAge = async (dobStr) => {
    const birth = new Date(dobStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    if (isNaN(age) || age < 16) return { ok: false };
    await setDoc(doc(db, "users", user.uid, "meta", "account"), { dob: dobStr, ageConfirmedAt: serverTimestamp() }, { merge: true });
    setAgeStatus("confirmed");
    return { ok: true };
  };

  // Once age is confirmed, figure out where this user stands on encryption:
  // never set up (needs to create one), set up but not unlocked on this
  // browser/session yet (needs to unlock), or already unlocked (ready).
  useEffect(() => {
    if (!user || ageStatus !== "confirmed") return;
    setEncStatus("checking");
    (async () => {
      const cachedRaw = sessionStorage.getItem(`solfri_enckey_${user.uid}`);
      const accountSnap = await getDoc(doc(db, "users", user.uid, "meta", "account")).catch(() => null);
      const account = accountSnap && accountSnap.exists() ? accountSnap.data() : null;

      if (!account || !account.encSalt) { setEncStatus("needed_create"); return; }

      if (cachedRaw) {
        try {
          const key = await importKey(cachedRaw);
          const valid = await checkVerifier(key, account.encVerifier, account.encVerifierIv);
          if (valid) { setEncKey(key); setEncStatus("ready"); return; }
        } catch { /* fall through to asking again */ }
      }
      setEncStatus("needed_unlock");
    })();
  }, [user?.uid, ageStatus]);

  const createPassphrase = async (passphrase) => {
    // New accounts get the dual-recovery scheme: a real, independent content
    // key, wrapped separately by the passphrase and by a freshly generated
    // backup code. Either one recovers the same key.
    const cek = await generateContentKey();
    const cekRaw = await exportKey(cek);

    const passphraseSalt = generateSalt();
    const passphraseWrapKey = await deriveKey(passphrase, passphraseSalt);
    const wrappedByPassphrase = await encryptObject(passphraseWrapKey, { cek: cekRaw });

    const backupCode = generateBackupCode();
    const backupSalt = generateSalt();
    const backupWrapKey = await deriveKey(backupCode, backupSalt);
    const wrappedByBackup = await encryptObject(backupWrapKey, { cek: cekRaw });

    await setDoc(doc(db, "users", user.uid, "meta", "account"), {
      encSalt: passphraseSalt,
      encWrappedKey: wrappedByPassphrase.encrypted, encWrappedKeyIv: wrappedByPassphrase.iv,
      encBackupSalt: backupSalt,
      encWrappedKeyBackup: wrappedByBackup.encrypted, encWrappedKeyBackupIv: wrappedByBackup.iv,
    }, { merge: true });

    sessionStorage.setItem(`solfri_enckey_${user.uid}`, cekRaw);
    setEncKey(cek);
    // Deliberately NOT setting encStatus("ready") here — doing so immediately
    // re-renders the parent, which stops rendering PassphraseGate at all
    // (its own render condition no longer matches), unmounting it before it
    // ever gets to show the backup code screen. The transition now happens
    // only once the user has acknowledged saving the code — see
    // acknowledgeBackupCode below, called from PassphraseGate's own button.
    return { ok: true, backupCode };
  };

  const acknowledgeBackupCode = () => setEncStatus("ready");

  const unlockPassphrase = async (passphrase) => {
    const accountSnap = await getDoc(doc(db, "users", user.uid, "meta", "account")).catch(() => null);
    const account = accountSnap && accountSnap.exists() ? accountSnap.data() : null;
    if (!account || !account.encSalt) return { ok: false, error: "Something went wrong loading your encryption settings." };

    if (account.encWrappedKey) {
      // New (dual-recovery) scheme — unwrap the real content key.
      const wrapKey = await deriveKey(passphrase, account.encSalt);
      const unwrapped = await decryptObject(wrapKey, account.encWrappedKey, account.encWrappedKeyIv);
      if (!unwrapped) return { ok: false, error: "That passphrase doesn't match. Please try again." };
      const cek = await importKey(unwrapped.cek);
      sessionStorage.setItem(`solfri_enckey_${user.uid}`, unwrapped.cek);
      setEncKey(cek);
      setEncStatus("ready");
      return { ok: true };
    }

    // Old scheme (accounts created before backup codes existed) — the key
    // IS directly the passphrase-derived key. Kept working exactly as
    // before; these accounts simply don't have a backup code available.
    const key = await deriveKey(passphrase, account.encSalt);
    const valid = await checkVerifier(key, account.encVerifier, account.encVerifierIv);
    if (!valid) return { ok: false, error: "That passphrase doesn't match. Please try again." };
    const rawKey = await exportKey(key);
    sessionStorage.setItem(`solfri_enckey_${user.uid}`, rawKey);
    setEncKey(key);
    setEncStatus("ready");
    return { ok: true };
  };

  const unlockWithBackupCode = async (backupCode) => {
    const accountSnap = await getDoc(doc(db, "users", user.uid, "meta", "account")).catch(() => null);
    const account = accountSnap && accountSnap.exists() ? accountSnap.data() : null;
    if (!account || !account.encWrappedKeyBackup) {
      return { ok: false, error: "No backup code is set up for this account." };
    }
    const wrapKey = await deriveKey(backupCode.trim(), account.encBackupSalt);
    const unwrapped = await decryptObject(wrapKey, account.encWrappedKeyBackup, account.encWrappedKeyBackupIv);
    if (!unwrapped) return { ok: false, error: "That backup code doesn't match. Please check it and try again." };
    const cek = await importKey(unwrapped.cek);
    sessionStorage.setItem(`solfri_enckey_${user.uid}`, unwrapped.cek);
    setEncKey(cek);
    setEncStatus("ready");
    return { ok: true };
  };

  // Decrypt one exchange for display. Gracefully handles older, pre-encryption
  // exchanges that were stored as plain fields — no migration needed, they
  // just display as-is until that session is next added to.
  const decryptExchange = async (ex) => {
    if (!ex.encrypted) return ex;
    const { encrypted, iv, ...meta } = ex;
    const plain = await decryptObject(encKey, encrypted, iv);
    if (!plain) return { ...meta, userMessage: "(couldn't decrypt this message)", empathy: "", strategies: [] };
    return { ...meta, ...plain };
  };
  const decryptSession = async (s) => ({ ...s, exchanges: await Promise.all((s.exchanges || []).map(decryptExchange)) });

  // Encrypt one exchange for storage — emotion/response_mode stay plaintext
  // (needed for bonsai scoring and theming without a decrypt round-trip),
  // everything else that could reveal what someone actually said or is going
  // through gets sealed into one encrypted blob.
  const encryptExchange = async (ex) => {
    const { emotion, response_mode, ...sensitive } = ex;
    const { encrypted, iv } = await encryptObject(encKey, sensitive);
    return { emotion, response_mode, encrypted, iv };
  };

  // Detect location and load crisis resources
  useEffect(() => {
    detectCountry().then(info => {
      setLocationInfo(info);
      const resources = getResourcesForCountry(info.countryCode);
      setCrisisResources(resources);
      // Language always defaults to English unless the user picks another
      // one manually via the language selector — no longer auto-set by location.
    });
  }, []);

  const handleLanguageChange = (code) => {
    setLanguage(code);
    localStorage.setItem("solfri_lang", code);
    if (user) {
      setDoc(doc(db, "users", user.uid, "meta", "account"), { language: code }, { merge: true }).catch(() => {});
    }
  };

  // Session reminder notifications
  useEffect(() => {
    if (!user || !sessionsLoaded) return;
    // Ask permission once
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    // Record last visit
    localStorage.setItem("solfri_last_visit", Date.now().toString());
    // Check if we should show a gentle in-app nudge based on last session date
    if (allSessions.length > 0 && "Notification" in window && Notification.permission === "granted") {
      const lastVisit = parseInt(localStorage.getItem("solfri_last_notified") || "0");
      const daysSinceNotified = (Date.now() - lastVisit) / 86400000;
      // Schedule a check — when the tab is open and 3+ days since last session
      const checkReminder = () => {
        const lastSessionTime = parseInt(localStorage.getItem("solfri_last_session") || "0");
        const daysSince = (Date.now() - lastSessionTime) / 86400000;
        if (lastSessionTime && daysSince >= 3 && daysSinceNotified >= 3) {
          new Notification("SolFri misses you 🌳", {
            body: "It's been a few days since your last check-in. Your bonsai is waiting — how are you feeling today?",
            icon: "/logo192.png",
          });
          localStorage.setItem("solfri_last_notified", Date.now().toString());
        }
      };
      checkReminder();
    }
  }, [user, sessionsLoaded]);

  // Load journey
  useEffect(() => {
    if (!user) { setJourney(null); return; }
    getDoc(doc(db, "users", user.uid, "meta", "journey"))
      .then(snap => { if (snap.exists()) setJourney(snap.data()); })
      .catch(() => {});
  }, [user?.uid]);

  const createJourney = async ({ a1, a2, a3 }) => {
    setJourneyCreating(true); setJourneyError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1600,
          system: buildIntakePlanPrompt({ a1, a2, a3, language, countryName: locationInfo.countryName }),
          messages: [{ role: "user", content: "Please create my journey." }],
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      const plan = JSON.parse(text.replace(/```json|```/g, "").trim());
      const j = {
        summary: plan.summary,
        milestones: plan.milestones.slice(0, 8).map(m => ({ ...m, status: "upcoming" })),
        currentIndex: 0,
        createdAt: new Date().toISOString(),
      };
      await setDoc(doc(db, "users", user.uid, "meta", "journey"), j);
      setJourney(j);
    } catch {
      setJourneyError("Couldn't create your journey. Please try again.");
    } finally { setJourneyCreating(false); }
  };

  const resetJourney = async () => {
    if (!window.confirm("Start a completely new journey? Your current path will be replaced.")) return;
    try { await deleteDoc(doc(db, "users", user.uid, "meta", "journey")); } catch {}
    setJourney(null); setSessionMode("open");
  };

  // Load sessions when user signs in AND encryption is unlocked — loading
  // before the key is ready would mean nothing could be decrypted yet.
  useEffect(() => {
    if (!user || encStatus !== "ready") { if (!user) { setAllSessions([]); setSessionsLoaded(false); } return; }
    setSessionsLoaded(false);
    const q = query(collection(db, "users", user.uid, "sessions"), orderBy("createdAt", "desc"), limit(30));
    getDocs(q)
      .then(async snap => {
        const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const decrypted = await Promise.all(raw.map(decryptSession));
        setAllSessions(decrypted);
        setSessionsLoaded(true);
      })
      .catch(() => setSessionsLoaded(true));
  }, [user?.uid, encStatus]);

  const handleSignOut = async () => {
    await signOut(auth);
    setAllSessions([]); setCurrentSession(null); setLatestResponse(null); setView("input"); setApiHistory([]);
  };

  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showCancelSub, setShowCancelSub] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const handleCancelSubscription = async () => {
    setCancelLoading(true); setCancelError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/cancel-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't cancel — please try again or contact support@solfri.app.");
      setCancelSuccess(true);
    } catch (e) {
      setCancelError(e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") { setDeleteError("Please type DELETE exactly to confirm."); return; }
    setDeleteLoading(true); setDeleteError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ confirm: "DELETE" }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't delete your account — please try again or contact support@solfri.app.");
      // The account is gone server-side, but the client SDK has no way to know
      // that on its own — force a local sign-out so the app falls through to
      // the signed-out view immediately, instead of appearing "still signed in"
      // to an account that no longer exists.
      setAllSessions([]); setCurrentSession(null); setLatestResponse(null); setApiHistory([]);
      setShowDeleteAccount(false); setDeleteConfirmText("");
      await signOut(auth).catch(() => {}); // token's already dead server-side; this is just local cleanup
    } catch (e) {
      setDeleteError(e.message);
      setDeleteLoading(false);
    }
  };

  const submit = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    const isStartingNewSession = !currentSession;
    if (!isStartingNewSession && (currentSession?.exchanges?.length||0) >= exchangeLimit) return;
    if (isStartingNewSession && tier === "free" && usageLoaded && sessionsThisMonth >= sessionLimit) {
      setError("You've used all 3 free sessions this month. Upgrade to SolFri Plus for unlimited sessions, or come back next month.");
      return;
    }
    setLoading(true); setError(null);
    rustleBonsai(); // the tree stirs as you share
    const newApiHistory = [...apiHistory, { role:"user", content:msg }];
    let jAddendum = "";
    if (sessionMode === "journey" && journey && journey.currentIndex < journey.milestones.length) {
      jAddendum = buildJourneyAddendum(journey, language);
    }
    try {
      const idToken = await auth.currentUser.getIdToken();
      const { stable, ctx, intentLine } = buildPrompt(allSessions, language, locationInfo.countryName, crisisResources?.resources?.[0]?.number, userIntent);
      const res = await fetch("/api/chat", {
        method:"POST",
        headers:{"Content-Type":"application/json", Authorization:`Bearer ${idToken}`},
        body:JSON.stringify({ model:"claude-sonnet-4-6", max_tokens:1200, system:stable, systemHistory:ctx, systemDynamic:intentLine + jAddendum, messages:newApiHistory }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Something went wrong. Please try again.");
      }
      const data = await res.json();
      const text = data.content?.map(b=>b.text||"").join("")||"";
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());

      const exchange = { userMessage:msg, emotion:parsed.emotion, empathy:parsed.empathy, validation:parsed.validation, pattern:parsed.pattern||null, gentle_truth:parsed.gentle_truth||null, response_mode:parsed.response_mode||null, invitation:parsed.invitation||null, strategies:parsed.strategies, closing:parsed.closing, crisis:parsed.crisis||null };

      let updatedSession;
      if (!currentSession) {
        const startedAt = new Date().toLocaleString("en-ZA",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});
        const storedExchange = await encryptExchange(exchange);
        const docRef = await addDoc(collection(db,"users",user.uid,"sessions"), { startedAt, createdAt:serverTimestamp(), exchanges:[storedExchange] });
        updatedSession = { id:docRef.id, startedAt, exchanges:[exchange] }; // plaintext locally
        setAllSessions(prev=>[updatedSession,...prev]);
        if (tier === "free") setSessionsThisMonth(n => n + 1); // mirror the server's counter locally, no reload needed
      } else {
        updatedSession = { ...currentSession, exchanges:[...currentSession.exchanges, exchange] }; // plaintext locally
        const storedExchanges = await Promise.all(updatedSession.exchanges.map(encryptExchange));
        await updateDoc(doc(db,"users",user.uid,"sessions",currentSession.id), { exchanges:storedExchanges });
        setAllSessions(prev=>prev.map(s=>s.id===currentSession.id?updatedSession:s));
      }

      setCurrentSession(updatedSession);
      setApiHistory([...newApiHistory,{role:"assistant",content:text}]);
      setLatestResponse(parsed);
      localStorage.setItem("solfri_last_session", Date.now().toString());
      if (sessionMode === "journey" && journey && parsed.milestone_progress) {
        let uj = { ...journey, milestones: [...journey.milestones] };
        if (Array.isArray(parsed.journey_adjustment)) {
          parsed.journey_adjustment.forEach(adj => {
            if (adj && typeof adj.index === "number" && adj.index > uj.currentIndex && uj.milestones[adj.index]) {
              uj.milestones[adj.index] = { ...uj.milestones[adj.index], title: adj.title || uj.milestones[adj.index].title, description: adj.description || uj.milestones[adj.index].description, adjusted: true };
            }
          });
        }
        if (parsed.milestone_progress.status === "completed") {
          uj.milestones[uj.currentIndex] = { ...uj.milestones[uj.currentIndex], status: "done", completedAt: new Date().toISOString() };
          uj.currentIndex = Math.min(uj.currentIndex + 1, uj.milestones.length);
        }
        setJourney(uj);
        try { await setDoc(doc(db, "users", user.uid, "meta", "journey"), uj); } catch {}
      }
      setView("session");
      setInput("");
    } catch(e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const endSession = () => { setView("input"); setCurrentSession(null); setApiHistory([]); setLatestResponse(null); setInput(""); setError(null); };

  // Loading
  if (!authReady) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F8FAFC",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{textAlign:"center"}}><p style={{fontSize:40,marginBottom:12}}>🌳</p><p style={{color:"#94A3B8",fontSize:14}}>Loading SolFri…</p></div>
    </div>
  );

  if (!user && !showAuth) return <LandingPage onGetStarted={() => setShowAuth(true)} />;

  if (!user) return <AuthScreen onBack={() => setShowAuth(false)} />;

  if (ageStatus === "checking") return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F8FAFC",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{textAlign:"center"}}><p style={{fontSize:40,marginBottom:12}}>🌳</p><p style={{color:"#94A3B8",fontSize:14}}>Loading SolFri…</p></div>
    </div>
  );

  if (ageStatus === "needed") return <AgeGate onConfirm={confirmAge} onSignOut={()=>signOut(auth)}/>;

  if (encStatus === "checking") return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F8FAFC",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{textAlign:"center"}}><p style={{fontSize:40,marginBottom:12}}>🌳</p><p style={{color:"#94A3B8",fontSize:14}}>Loading SolFri…</p></div>
    </div>
  );
  if (encStatus === "needed_create") return <PassphraseGate mode="create" onSubmit={createPassphrase} onBackupCodeSubmit={unlockWithBackupCode} onBackupCodeAcknowledged={acknowledgeBackupCode} onSignOut={()=>signOut(auth)}/>;
  if (encStatus === "needed_unlock") return <PassphraseGate mode="unlock" onSubmit={unlockPassphrase} onBackupCodeSubmit={unlockWithBackupCode} onSignOut={()=>signOut(auth)}/>;

  if (!sessionsLoaded) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F8FAFC",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif"}}>
      <div style={{textAlign:"center"}}><p style={{fontSize:40,marginBottom:12}}>🌳</p><p style={{color:"#94A3B8",fontSize:14}}>Loading your sessions…</p></div>
    </div>
  );

  const t = theme(latestResponse?.emotion);
  // Deeper than the old #FDF8F3: white cards sitting on near-white read as
  // flat, with the card edge only visible via its border. A warmer, deeper
  // base lets the cards lift off the page on their own.
  const bgColor = view==="session" ? t.bg : "#F4EAE0";
  const lastSession = allSessions[0];
  const treeStage = calcTreeStage(allSessions, journey?.currentIndex || 0);
  const stageInfo = STAGE_LABELS[treeStage];

  return (
    <div style={{minHeight:"100vh",
      background:`radial-gradient(ellipse 110% 55% at 12% -8%, rgba(212,165,116,0.20), transparent 62%), radial-gradient(ellipse 85% 45% at 92% 4%, rgba(196,133,106,0.16), transparent 58%), radial-gradient(ellipse 95% 65% at 50% 108%, rgba(122,158,126,0.13), transparent 62%), ${bgColor}`,
      transition:"background 0.8s ease",fontFamily:"'Zen Kaku Gothic New',-apple-system,sans-serif",display:"flex",flexDirection:"column",alignItems:"center",padding:"24px 16px 80px"}}>

      <div style={{width:"100%",maxWidth:contentWidth,marginBottom:16}}>
        {/* Top bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:8}}>
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowProfileMenu(v=>!v)} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:0}}>
              {user.photoURL
                ? <img src={user.photoURL} alt="" style={{width:32,height:32,borderRadius:"50%",border:"2px solid #E2E8F0"}}/>
                : <div style={{width:32,height:32,borderRadius:"50%",background:"#A0673A",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700}}>{user.displayName?.[0]?.toUpperCase()||"U"}</div>
              }
              <span style={{fontSize:11,color:"#94A3B8"}}>▾</span>
            </button>

            {showProfileMenu && (
              <>
                <div onClick={()=>setShowProfileMenu(false)} style={{position:"fixed",inset:0,zIndex:40}}/>
                <div style={{position:"absolute",top:"calc(100% + 8px)",left:0,background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:14,boxShadow:"0 2px 4px rgba(92,61,46,0.08), 0 8px 20px rgba(92,61,46,0.12), 0 22px 50px rgba(92,61,46,0.14)",minWidth:220,zIndex:41,overflow:"hidden"}}>
                  {usageLoaded && tier==="free" && (
                    <>
                      <div style={{padding:"12px 16px",background:"#FDF8F3"}}>
                        <p style={{fontSize:11.5,color:"#8A7060",margin:"0 0 8px",fontWeight:600}}>
                          {Math.max(sessionLimit-sessionsThisMonth,0)} of {sessionLimit} free sessions left this month
                        </p>
                        <button onClick={()=>{startUpgrade();setShowProfileMenu(false);}} disabled={upgradeLoading} style={{width:"100%",background:upgradeLoading?"#E8C99A":"#D4A574",color:"#fff",border:"none",borderRadius:9,padding:"8px 0",fontSize:12.5,fontWeight:700,cursor:upgradeLoading?"wait":"pointer",fontFamily:"inherit"}}>
                          {upgradeLoading ? "Opening…" : "☀️ Upgrade now — R55/mo"}
                        </button>
                      </div>
                      <div style={{borderTop:"1px solid #F0E0D0"}}/>
                    </>
                  )}
                  {usageLoaded && tier==="plus" && (
                    <>
                      <div style={{padding:"12px 16px",background:"#FDF8F3"}}>
                        <p style={{fontSize:11.5,color:"#3F6B44",margin:0,fontWeight:700}}>☀️ SolFri Plus — unlimited sessions</p>
                      </div>
                      <div style={{borderTop:"1px solid #F0E0D0"}}/>
                    </>
                  )}
                  <button onClick={()=>{setShowHistory(true);setShowProfileMenu(false);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#5C3D2E",cursor:"pointer",fontFamily:"inherit"}}>
                    {"📖 "}{allSessions.length>0?`${allSessions.length} session${allSessions.length!==1?"s":""}`:"History"}
                  </button>
                  <button onClick={()=>{setShowTeamPurchase(true);setShowProfileMenu(false);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#5C3D2E",cursor:"pointer",fontFamily:"inherit"}}>
                    🏢 For teams
                  </button>
                  <button onClick={()=>{setShowLangSelector(true);setShowProfileMenu(false);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#5C3D2E",cursor:"pointer",fontFamily:"inherit"}}>
                    {LANGUAGES.find(l=>l.code===language)?.flag || "🌐"} Language
                  </button>
                  {tier !== "plus" && (
                    <button onClick={()=>{setShowRedeemModal(true);setShowProfileMenu(false);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#5C3D2E",cursor:"pointer",fontFamily:"inherit"}}>
                      🎟️ Redeem a code
                    </button>
                  )}
                  <div style={{borderTop:"1px solid #F0E0D0"}}/>
                  {isIndividualSubscriber && !isCancelled && (
                    <button onClick={()=>{setShowCancelSub(true);setShowProfileMenu(false);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#D97706",fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                      Cancel subscription
                    </button>
                  )}
                  <button onClick={()=>{setShowDeleteAccount(true);setShowProfileMenu(false);}} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#DC2626",fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    Delete my account
                  </button>
                  <div style={{borderTop:"1px solid #F0E0D0"}}/>
                  <button onClick={handleSignOut} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"11px 16px",fontSize:13,color:"#94A3B8",cursor:"pointer",fontFamily:"inherit"}}>
                    Sign out
                  </button>
                  <div style={{borderTop:"1px solid #F0E0D0"}}/>
                  <div style={{padding:"10px 16px",display:"flex",gap:10}}>
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{color:"#94A3B8",fontSize:11,textDecoration:"underline"}}>Privacy Policy</a>
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{color:"#94A3B8",fontSize:11,textDecoration:"underline"}}>Terms</a>
                  </div>
                </div>
              </>
            )}
          </div>
          <div style={{textAlign:"center"}}>
            <h1 style={{fontFamily:"'Zen Old Mincho',Georgia,serif",fontSize:30,fontWeight:600,letterSpacing:"0.2em",color:"#5C3D2E",margin:0,lineHeight:1.15}}>SolFri</h1>
            <p style={{fontFamily:"'Zen Kaku Gothic New',sans-serif",color:"#8F7866",fontSize:8.5,margin:"5px 0 0",letterSpacing:"0.4em",fontWeight:400,textTransform:"lowercase"}}>set your soul free</p>
          </div>
          <div style={{display:"flex",gap:6}}>
            {crisisResources && (
              <button onClick={()=>setShowCrisisPanel(true)} style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:20,padding:"5px 12px",fontSize:11,color:"#DC2626",cursor:"pointer",fontWeight:700,fontFamily:"inherit"}}>
                🆘 Help
              </button>
            )}
          </div>
        </div>

        {/* The one place the design raises its voice: the ensō frames the
            bonsai, drawn once on arrival. Borderless and generously spaced —
            in this tradition the empty area is the composition, not waste. */}
        {view==="input" && (
          <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF6EE 100%)",borderRadius:26,padding:isNarrow?"26px 20px 22px":"34px 32px 28px",boxShadow:"0 1px 2px rgba(92,61,46,0.045), 0 6px 16px rgba(92,61,46,0.05), 0 20px 44px rgba(92,61,46,0.07)",marginBottom:18,display:"flex",flexDirection:isNarrow?"column":"row",alignItems:"center",gap:isNarrow?18:34}}>
            <div style={{flexShrink:0,position:"relative",display:"flex",alignItems:"center",justifyContent:"center",
              width:isNarrow?200:224,height:isNarrow?200:224}}>
              <Enso size={isNarrow?196:220}/>
              <div key={bonsaiRustle} className="bonsai-anim bonsai-rustling" style={{position:"relative",zIndex:1}}>
                <BonsaiTree stage={treeStage} size={isNarrow?136:152}/>
              </div>
            </div>
            <div style={{flex:1,minWidth:0,textAlign:isNarrow?"center":"left"}}>
              <p style={{fontFamily:"'Zen Old Mincho',Georgia,serif",fontSize:isNarrow?15:16.5,color:"#5C3D2E",lineHeight:1.75,margin:"0 0 14px",fontWeight:500}}>
                {!currentSession && (lastSession
                  ? <>Welcome back, {user.displayName?.split(" ")[0]}.<br/>
                      <span style={{fontSize:13.5,color:"#8A7060"}}>Last time you felt <span style={{color:theme(lastSession.exchanges?.[0]?.emotion).accent,textTransform:"capitalize"}}>{lastSession.exchanges?.[0]?.emotion}</span>. How are you today?</span></>
                  : <>Welcome, {user.displayName?.split(" ")[0]}.<br/>
                      <span style={{fontSize:13.5,color:"#8A7060"}}>This is yours alone. Your bonsai grows as you do.</span></>
                )}
              </p>
              <div style={{display:"flex",alignItems:"center",gap:10,justifyContent:isNarrow?"center":"flex-start",flexWrap:"wrap"}}>
                <span style={{fontFamily:"'Zen Old Mincho',Georgia,serif",fontSize:12,color:"#A0673A",letterSpacing:"0.08em",textTransform:"uppercase",fontWeight:600}}>
                  Stage {treeStage}
                </span>
                <span style={{width:22,height:1,background:"#E0CDBA"}}/>
                <span style={{fontSize:12.5,color:"#8A7060",letterSpacing:"0.02em"}}>{stageInfo.label}</span>
              </div>
              <p style={{color:"#A08070",fontSize:12.5,margin:"9px 0 0",lineHeight:1.65,fontStyle:"italic"}}>
                {stageInfo.message}
              </p>
            </div>
          </div>
        )}

        {view==="input" && (
          <JourneyCard journey={journey} onOpen={()=>setShowJourney(true)}/>
        )}
      </div>

      <div style={{width:"100%",maxWidth:contentWidth}}>
        {/* Only appears when genuinely out of sessions — blocks the chat
            below it. Everywhere else, upgrade lives only in the profile menu. */}
        {view==="input" && usageLoaded && tier==="free" && sessionsThisMonth>=sessionLimit && (
          <div style={{background:"linear-gradient(135deg,#FDF4E7,#FDF8F3)",borderRadius:20,padding:24,border:"1.5px solid #D4A574",marginBottom:16,textAlign:"center"}}>
            <p style={{fontSize:28,margin:"0 0 8px"}}>☀️</p>
            <h3 style={{fontSize:16,fontWeight:800,color:"#5C3D2E",margin:"0 0 8px"}}>You've used your 3 free sessions this month</h3>
            <p style={{fontSize:13,color:"#8A6A2E",lineHeight:1.6,margin:"0 0 16px"}}>Upgrade to SolFri Plus for unlimited sessions and longer conversations — or come back next month for 3 more, on us.</p>
            {upgradeError && <p style={{color:"#DC2626",fontSize:12,marginBottom:10}}>⚠️ {upgradeError}</p>}
            <button onClick={startUpgrade} disabled={upgradeLoading} style={{background:upgradeLoading?"#E8C99A":"#D4A574",color:"#fff",border:"none",borderRadius:12,padding:"12px 24px",boxShadow:upgradeLoading?"none":"0 1px 2px rgba(92,61,46,0.12), 0 4px 12px rgba(212,165,116,0.34), 0 10px 24px rgba(212,165,116,0.20)",fontSize:14,fontWeight:700,cursor:upgradeLoading?"wait":"pointer",fontFamily:"inherit"}}>
              {upgradeLoading ? "Opening secure checkout…" : "Upgrade to SolFri Plus — R55/month"}
            </button>
            <p style={{fontSize:11,color:"#A08858",margin:"10px 0 0"}}>Secure payment via Paystack. Cancel anytime.</p>
            <button onClick={()=>setShowRedeemModal(true)} style={{background:"none",border:"none",color:"#A08858",fontSize:12,textDecoration:"underline",cursor:"pointer",fontFamily:"inherit",marginTop:12}}>
              Have a voucher or team code?
            </button>
            {teamJoinedName && (
              <p style={{fontSize:12,color:"#3F6B44",marginTop:12,fontWeight:600}}>✓ You've joined {teamJoinedName}'s SolFri Plus team plan</p>
            )}
          </div>
        )}
        {view==="input" && (
          <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:24,boxShadow:"0 1px 2px rgba(92,61,46,0.05), 0 4px 10px rgba(92,61,46,0.055), 0 14px 34px rgba(92,61,46,0.07)",marginBottom:16,opacity:(usageLoaded && tier==="free" && sessionsThisMonth>=sessionLimit)?0.5:1,pointerEvents:(usageLoaded && tier==="free" && sessionsThisMonth>=sessionLimit)?"none":"auto"}}>
            <p style={{color:"#64748B",fontSize:14,marginBottom:14,lineHeight:1.6}}>How are you feeling right now? {"There's"} no judgment here. {"💙"}</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
              {(showAllEmotions ? STARTERS : STARTERS.filter(s=>s.featured)).map(({ w, tone })=>{
                const seeded = `I'm feeling ${w.toLowerCase()} `;
                const active = input.toLowerCase().startsWith(`i'm feeling ${w.toLowerCase()}`);
                const good = tone === "good";
                return (
                  <button key={w} onClick={()=>setInput(seeded)}
                    style={{
                      background: active ? (good?"#3F6B44":"#5C3D2E") : (good?"#F0F7F1":"#FDF8F3"),
                      border: `1px solid ${active ? (good?"#3F6B44":"#5C3D2E") : (good?"#D3E5D5":"#F0E0D0")}`,
                      borderRadius:20, padding:"6px 13px", fontSize:12,
                      color: active ? "#fff" : (good?"#3F6B44":"#A08070"),
                      cursor:"pointer", fontWeight:500, fontFamily:"inherit", transition:"all 0.15s",
                    }}>
                    {w}
                  </button>
                );
              })}
              <button onClick={()=>setShowAllEmotions(v=>!v)}
                style={{background:"none",border:"1px dashed #E8D5C4",borderRadius:20,padding:"6px 13px",fontSize:12,color:"#A08070",cursor:"pointer",fontWeight:600,fontFamily:"inherit"}}>
                {showAllEmotions ? "− less" : "+ more"}
              </button>
            </div>
            {journey && journey.currentIndex < journey.milestones.length && (
              <button onClick={()=>setSessionMode(m=>m==="journey"?"open":"journey")} style={{width:"100%",textAlign:"left",background:sessionMode==="journey"?"#F0F7F1":"#FDF8F3",border:`1.5px solid ${sessionMode==="journey"?"#7A9E7E":"#F0E0D0"}`,borderRadius:10,padding:"9px 13px",fontSize:12,color:sessionMode==="journey"?"#3F6B44":"#A08070",fontWeight:600,cursor:"pointer",fontFamily:"inherit",marginBottom:12}}>
                {sessionMode==="journey"
                  ? `🧭 Journey session — Milestone ${journey.currentIndex+1}: ${journey.milestones[journey.currentIndex].title}  (tap for open session)`
                  : "💬 Open session — tap to switch to your journey milestone"}
              </button>
            )}
            <div style={{position:"relative"}}>
              <textarea value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();submit();}}} placeholder="Tell me what's on your mind — type or tap 🎤 to speak…" rows={4}
                style={{width:"100%",border:"1.5px solid #E8D5C4",borderRadius:12,padding:"13px 15px",paddingRight:48,fontSize:15,lineHeight:1.6,color:"#1E293B",resize:"none",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#FFFAF7"}}
                onFocus={e=>e.target.style.borderColor="#A0673A"} onBlur={e=>e.target.style.borderColor="#E8D5C4"}/>
              <VoiceMic value={input} setValue={setInput} language={language}/>
            </div>
            {/* Asked only AFTER they've shared something — asking what someone
                needs before they've said anything is backwards. */}
            {input.trim() && (
              <div style={{marginTop:14}}>
                <p style={{color:"#64748B",fontSize:13,marginBottom:9,lineHeight:1.5}}>What would help most right now?</p>
                <IntentChips value={userIntent} onChange={setUserIntent}/>
              </div>
            )}
            {error && <p style={{color:"#EF4444",fontSize:13,marginTop:8}}>{"⚠️"} {error}</p>}
            <button onClick={submit} disabled={loading||!input.trim()} style={{marginTop:12,width:"100%",background:loading||!input.trim()?"#CBD5E1":"#A0673A",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",boxShadow:loading||!input.trim()?"none":"0 1px 2px rgba(92,61,46,0.12), 0 4px 12px rgba(160,103,58,0.28), 0 10px 24px rgba(160,103,58,0.16)",fontSize:15,fontWeight:700,cursor:loading||!input.trim()?"not-allowed":"pointer",fontFamily:"inherit"}}>
              {loading?"Listening…":"Share with SolFri →"}
            </button>
          </div>
        )}

        {view==="session" && latestResponse && (
          <ResponseCard response={latestResponse} onContinue={submit} onEndSession={endSession} exchangeCount={currentSession?.exchanges?.length||0} exchangeLimit={exchangeLimit} input={input} setInput={setInput} loading={loading} userIntent={userIntent} setUserIntent={setUserIntent} language={language}/>
        )}

        <p style={{color:"#7A6046",fontSize:11,textAlign:"center",lineHeight:1.6}}>
          SolFri is a supportive companion, not a substitute for professional care.<br/>
          {crisisResources?.resources?.[0]
            ? <>In crisis? {crisisResources.resources[0].name}: <strong>{crisisResources.resources[0].number}</strong>, or tap 🆘 Help above for more options.</>
            : <>In crisis? Tap 🆘 Help above for support resources in your area.</>}
        </p>
      </div>

      {showHistory && <HistoryPanel sessions={allSessions} onClose={()=>setShowHistory(false)} userName={user?.displayName} treeStage={treeStage} stageLabel={stageInfo.label} crisisLine={crisisResources ? `${crisisResources.resources[0]?.name}: ${crisisResources.resources[0]?.number}` : "Contact your local emergency services or a crisis helpline"}/>}
      {showTeamPurchase && <TeamPurchaseModal user={user} publicKey={PAYSTACK_PUBLIC_KEY} onClose={()=>setShowTeamPurchase(false)}/>}
      {showDeleteAccount && <DeleteAccountModal confirmText={deleteConfirmText} setConfirmText={setDeleteConfirmText} loading={deleteLoading} error={deleteError} onConfirm={handleDeleteAccount} onClose={()=>{setShowDeleteAccount(false);setDeleteConfirmText("");setDeleteError(null);}}/>}
      {showCancelSub && <CancelSubscriptionModal loading={cancelLoading} error={cancelError} success={cancelSuccess} onConfirm={handleCancelSubscription} onClose={()=>{setShowCancelSub(false);setCancelError(null);if(cancelSuccess){setIsCancelled(true);setCancelSuccess(false);}}}/>}
      <InstallPrompt/>
      {showLangSelector && <LanguageSelector currentLang={language} onSelect={handleLanguageChange} onClose={()=>setShowLangSelector(false)}/>}
      {showRedeemModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(30,20,10,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,padding:16}} onClick={()=>setShowRedeemModal(false)}>
          <div style={{background:"linear-gradient(170deg,#FFFDFB 0%,#FFF8F2 100%)",borderRadius:20,padding:26,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontSize:17,fontWeight:800,color:"#5C3D2E",margin:"0 0 6px",textAlign:"center"}}>Redeem a code</h3>
            <p style={{fontSize:12.5,color:"#8A7060",lineHeight:1.6,margin:"0 0 18px",textAlign:"center"}}>Have a personal voucher or a code from your company? Enter it below.</p>

            <p style={{fontSize:12,color:"#5C3D2E",fontWeight:700,margin:"0 0 6px"}}>Personal voucher code</p>
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <input type="text" value={voucherCode} onChange={e=>setVoucherCode(e.target.value)} placeholder="SOLFRI-XXXXXX" autoCapitalize="characters"
                style={{flex:1,border:"1.5px solid #E8D5C4",borderRadius:10,padding:"10px 12px",fontSize:13,color:"#5C3D2E",outline:"none",fontFamily:"inherit",background:"#fff",textTransform:"uppercase"}}/>
              <button onClick={redeemVoucher} disabled={voucherLoading} style={{background:"#5C3D2E",color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontSize:13,fontWeight:700,cursor:voucherLoading?"wait":"pointer",fontFamily:"inherit"}}>
                {voucherLoading?"…":"Redeem"}
              </button>
            </div>
            {voucherError && <p style={{color:"#DC2626",fontSize:11.5,margin:"0 0 12px"}}>⚠️ {voucherError}</p>}

            <div style={{borderTop:"1px solid #F0E0D0",margin:"16px 0"}}/>

            <p style={{fontSize:12,color:"#5C3D2E",fontWeight:700,margin:"0 0 6px"}}>Company / team code</p>
            <div style={{display:"flex",gap:6,marginBottom:6}}>
              <input type="text" value={teamCode} onChange={e=>setTeamCode(e.target.value)} placeholder="Your team's code" autoCapitalize="characters"
                style={{flex:1,border:"1.5px solid #E8D5C4",borderRadius:10,padding:"10px 12px",fontSize:13,color:"#5C3D2E",outline:"none",fontFamily:"inherit",background:"#fff",textTransform:"uppercase"}}/>
              <button onClick={joinTeam} disabled={teamLoading} style={{background:"#5C3D2E",color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontSize:13,fontWeight:700,cursor:teamLoading?"wait":"pointer",fontFamily:"inherit"}}>
                {teamLoading?"…":"Join"}
              </button>
            </div>
            {teamError && <p style={{color:"#DC2626",fontSize:11.5,margin:"0 0 12px"}}>⚠️ {teamError}</p>}
            {teamJoinedName && <p style={{fontSize:12,color:"#3F6B44",margin:"0 0 12px",fontWeight:600}}>✓ You've joined {teamJoinedName}'s SolFri Plus team plan</p>}

            <button onClick={()=>setShowRedeemModal(false)} style={{width:"100%",background:"none",border:"none",color:"#8A7060",fontSize:13,cursor:"pointer",fontFamily:"inherit",marginTop:6}}>Close</button>
          </div>
        </div>
      )}
      {showCrisisPanel && crisisResources && <CrisisPanel resources={crisisResources} onClose={()=>setShowCrisisPanel(false)}/>}
      {showJourney && (
        <JourneyModal
          journey={journey}
          onClose={()=>setShowJourney(false)}
          onCreate={createJourney}
          onStartMilestone={()=>{ setSessionMode("journey"); setShowJourney(false); }}
          onReset={resetJourney}
          creating={journeyCreating}
          error={journeyError}
        />
      )}
      {loading && <ListeningOverlay/>}
    </div>
  );
}
